// native-recorder.ts — Web-Adapter für den nativen Alltags-Recorder (Phase 2).
//
// Läuft NUR in der nativen Hülle (`isNativePlatform()`); im Browser/PWA No-op
// (eine WebView kann im Hintergrund nicht aufnehmen — dafür gibt es das native
// PawbotRecorder-Plugin). Spiegelt dessen Schnittstelle (pawbot-recorder.ts).

import {
  gleicheMitZustandAb,
  istNutzerPause,
  leseNativenGrund,
  leseZuletztLief,
  merkeBesitzer,
  meldePauseGrund,
  meldePausenStart,
  merkeBesitzerSchwach,
  urteilFuerWache,
  merkeLaeuft,
  merkeNeubeginn,
  vergissBesitzer,
} from "./pause-besitzer.js";
import { isNativePlatform, getCapacitorPlugin } from "./native-voice.js";
import {
  loeseWiedergabeHalt,
  setAlltagNimmtAuf,
  wiedergabeHaltStand,
} from "./playback-hifi.js";
import { aktuellerTon } from "./ton-leitstand.js";
import { mikroBeimVorlesenMerken } from "./mikro-beim-vorlesen.js";
import { meldeMikroAnlass } from "./mikro-spur.js";
import { meldeAufnahmeLauf } from "./aufnahme-lauf.js";
import { meldePauseFuerPush } from "./aufnahme-pause-push.js";

export type RecorderState =
  | "idle"
  | "recording"
  | "paused"
  | "finishing"
  | "error";

interface RecorderCallbacks {
  onState?: (state: RecorderState) => void;
  onUploadProgress?: (info: { pending: number; uploaded: number }) => void;
  onError?: (err: Error) => void;
}

// Spiegel des nativen Plugin-Vertrags (pawbot-recorder.ts).
interface PawbotRecorderPlugin {
  start(opts: { maxDurationSec?: number }): Promise<void>;
  /**
   * ⚠️ `nutzer` entscheidet, ob die Tages-Absicht mitstirbt. Ein Mensch, der
   *    auf Stopp drueckt, beendet den Tag; die Rettung einer toten Sitzung
   *    nicht. Bis zum 17.09. sahen beide gleich aus — und beide beendeten
   *    die Daueraufnahme.
   */
  stop(opts?: { nutzer?: boolean; grund?: string }): Promise<void>;
  pause(): Promise<void>;
  resume(): Promise<void>;
  flush(): Promise<void>;
  getState(): Promise<{
    state?: string;
    startedAt?: number;
    pausedAt?: number;
    dayIntent?: boolean;
    /** Warum pausiert (neuere Huelle); fehlt bei alten Huellen. */
    grund?: string;
  }>;
  setPlaybackHiFi?(opts: { active: boolean }): Promise<AudioSessionState>;
  /** Ticket 744: Display wachhalten (true) oder wieder schlafen lassen (false). */
  setDisplayWach?(opts: { wach: boolean }): Promise<{ wach: boolean }>;
  /** PTT aus der laufenden Sitzung (05.09.2026) — siehe holePttNativ(). */
  pttStart?(): Promise<{ startedAt?: number }>;
  pttStop?(): Promise<{ base64: string; mime?: string; bytes?: number; durationMs?: number }>;
  pttCancel?(): Promise<void>;
  pttLevel?(): Promise<{ level?: number }>;
  setAlwaysRecord?(opts: { active: boolean }): Promise<{ active: boolean }>;
  /**
   * `rekorderMessung: true` (26.09.2026, Ticket 2615, ab Build 87): die
   * Messwerte des ersten abgelehnten Starts einer Folge mitgeben — und in
   * der App leeren. Ohne die Bitte bleiben sie dort liegen.
   */
  getAudioSessionState?(opts?: { rekorderMessung?: boolean }): Promise<AudioSessionState>;
  /** 04.10.2026 (ab Build 103): Energiesparmodus, Hitze, Akku, Bildrate des Bildschirms. */
  geraeteZustand?(): Promise<GeraeteZustandNativ>;
  /** Ticket 2643 (ab Build 93): die Einstellungen der App öffnen. */
  oeffneAppEinstellungen?(): Promise<{ ok?: boolean }>;
  widgetUpdate?(opts: {
    lastTitle?: string;
    lastTimeHHMM?: string;
    countToday?: number;
    openTasks?: number;
    uiBg?: string;
    uiAccent?: string;
    uiStar?: string;
    uiInk?: string;
    uiInkSoft?: string;
    uiIsDark?: boolean;
  }): Promise<void>;
  addListener(
    event: "recorderState" | "uploadProgress" | "error",
    cb: (data: unknown) => void,
  ): Promise<{ remove: () => void }>;
}

const VALID_STATES: ReadonlySet<string> = new Set<RecorderState>([
  "idle",
  "recording",
  "paused",
  "finishing",
  "error",
]);

// Sicherheits-Auto-Stopp (Default): 26 h. 2026-07-13 (Martin, Reformation C):
// Ganztages-/24h-Dauerlauf. Der native Recorder setzt nach jeder unfreiwilligen
// Unterbrechung (Anruf, App-Kill durch Sicherung/Update/Reboot) automatisch fort
// (persistierte Tages-Absicht) — dieser Cap ist nur der Runaway-Schutz.
const DEFAULT_MAX_DURATION_SEC = 93600; // 26h

/** Antwort von `geraeteZustand` (nur Zahlen und Stichwörter, keine Inhalte). */
export interface GeraeteZustandNativ {
  energiesparmodus?: boolean;
  hitze?: string;
  akku?: number;
  ladung?: string;
  maxFps?: number;
}

function getRecorder(): PawbotRecorderPlugin | null {
  return getCapacitorPlugin<PawbotRecorderPlugin>("PawbotRecorder");
}

let listeners: Array<{ remove: () => void }> = [];
let currentState: RecorderState = "idle";
// Zeitstempel (ms) des laufenden Aufnahme-Starts — für die laufende Uhr beim
// ERNEUTEN Öffnen des Overlays. Bleibt über pause/resume erhalten, 0 wenn idle.
let recordingStartedAt = 0;
// 2026-07-14 (Martin, "Uhr 0:00 beim Oeffnen in der Pause"): Zeitpunkt (ms)
// des Pause-Druecks vom nativen Recorder — 0 = nicht pausiert. Damit kann
// die Overlay-Uhr auch beim Oeffnen MITTEN in einer Pause korrekt auf den
// Pausen-Moment eingefroren werden.
let recordingPausedAt = 0;
// 2026-07-14 (Martin, "Aufnahme starb nach 8.8s Audio"): Tages-Absicht des
// nativen Recorders — true auch in Unterbrechungs-Luecken (state=idle,
// Auto-Restart wartet aufs freie Mikro). ptt.ts haelt das Warm-Mikro fern.
let dayIntentActive = false;
// Aktive Callbacks der laufenden Aufnahme — so kann auch stopRecording() (ohne eigenes
// cb) den UI-Zustand melden, und der terminale „idle"-Event findet seinen Empfänger.
let activeCb: RecorderCallbacks = {};
// 2026-07-04 (#35): DAUERHAFTE Beobachter (z.B. Header-Indikator) — werden
// im Gegensatz zu activeCb NIE von einem neuen attach() verdraengt. Vorher
// verdraengte das Aufnahme-Overlay den Header-Callback, der Header verpasste
// das terminale idle und zeigte "Aufnahme" nach dem Stopp weiter an.
const globalObservers = new Set<(s: RecorderState) => void>();
// Letzter Upload-Stand (offene/hochgeladene Häppchen) als Modul-State, damit
// „Mein Alltag" ihn via getUploadInfo() lesen kann — auch ohne aktiven
// Observer. Im Flugmodus-Fall liegen nach dem Stoppen Häppchen offen, bis das
// Netz zurückkommt; das soll sichtbar sein.
let lastUploadInfo = { pending: 0, uploaded: 0 };
// 2026-07-14 (Martin, "weiss nicht was Sache ist"): Zeitpunkt des letzten
// uploadProgress-Events — die BEWEIS-Zeile im Overlay ("zuletzt gesichert
// vor Xs") zeigt echten Datenfluss statt eines blossen Zustands-Flags.
let lastUploadAt = 0;

/** Zeitstempel (ms) des letzten gesicherten Haeppchens; 0 = noch keins. */
export function getLastUploadAt(): number {
  return lastUploadAt;
}

// 2026-07-12 (Martin, "Pausiert direkt nach Start"): Zustand zusaetzlich
// als window-Event melden — ptt.ts muss das Warm-Mikro freigeben, solange
// der Tages-Recorder laeuft, darf native-recorder aber nicht importieren
// (Zyklus). Gleiches Muster wie voice-v8:tts-audio-start.
function emitStateEvent(s: RecorderState): void {
  /*
   * ⚠️ 07.09.2026 — ZUERST direkt melden, dann das Ereignis.
   *
   * Die Tonanlage (playback-hifi.ts) muss wissen, ob eine Aufnahme laeuft:
   * in der Kategorie `.playback` gibt iOS kein Mikrofon her. Sie hing bis
   * heute am Ereignis unten — und wurde selbst erst geladen, wenn zum
   * ersten Mal etwas abgespielt wurde. Lief die Aufnahme da schon, hat sie
   * deren Start nie gehoert. Martin verlor so fuenf Minuten: *„Jetzt macht
   * er schon waehrenddessen Pause, waehrend ich aufnehme."*
   */
  setAlltagNimmtAuf(
    s === "recording" || s === "paused" || s === "finishing" || dayIntentActive,
  );
  try {
    window.dispatchEvent(
      new CustomEvent("voice-v8:recorder-state", {
        detail: { state: s, dayIntent: dayIntentActive },
      }),
    );
  } catch {
    /* kein window (Tests) */
  }
}

/**
 * Eine Pause, die NIEMAND von uns gewollt hat. (21.09.2026, Ticket 2434)
 *
 * Martin, 20.09. um 21:00: *„Jetzt fangen bei den Aufnahmen schon wieder
 * diese verdammten Pausen an. Ich nehme auf und dann ploetzlich steht Pause
 * dort. Wieso verdammt?"* Es war der dritte Anlauf; am Archiv nachgemessen
 * hat das Geraet zwischen 19:55 und 21:00 kein einziges Haeppchen gesendet
 * und dann 40 auf einmal, als die App wieder in den Vordergrund kam.
 *
 * ⚠️ DIE PAUSE STAND NIRGENDS IM PROTOKOLL. Der native Teil meldet dem
 *    Server nur Stopp, Fortsetzen und Neubeginn. Eine Unterbrechung durch
 *    iOS — Anruf, Hintergrund, Verlust der Tonsitzung — und der Moment, in
 *    dem die Anzeige auf „Pause" springt, kamen nie an. Jede Aussage ueber
 *    die Ursache waere geraten gewesen.
 *
 * Hier wird das unterscheidbar, OHNE dass die native Huelle etwas Neues
 * koennen muss: `micPauseGruende` weiss, ob WIR pausiert haben (Vorlesen,
 * Push-to-Talk, Diktat, Tiefe Frage, Selbstheilung). Ist die Menge leer und
 * der Zustand springt auf „paused", kam die Pause von aussen.
 */
/*
 * ⚠️ NUR die FREMDE Pause wird hier gemeldet. Unsere eigenen Pausen melden
 *    sich schon dort, wo sie entstehen (`schonung-pause art=…` in
 *    ton-rekorder-schonung.ts). Beides zu melden hiesse, jede Wiedergabe
 *    zweimal ins Journal zu schreiben — genau die Flut, gegen die der Sturm
 *    vom 20.09. (87 Zeilen in zwei Minuten) abgestellt wurde.
 */
export function fremdePauseMeldung(
  vorher: RecorderState,
  nachher: RecorderState,
  eigeneGruende: readonly string[],
  zuletztAusgetragen: { grund: string; vorMs: number } | null = null,
  /**
   * Der Grund, den die App mitschickt (seit Build 80: „unterbrechung-andere-
   * sitzung", „mikro-verweigert" …). 25.09.2026: Er kam an, wurde hier aber
   * nie gelesen — 21 fremde Pausen seit dem 21.09. standen als „unbekannt"
   * im Journal, auch Martins Telefonat heute um 09:04.
   */
  nativerGrund?: string,
  /**
   * Wie lange Martins eigener Pause-Knopf her ist (ms), null = gar nicht.
   * 27.09.2026 (Ticket 2708): Der Knopf pausiert ohne eingetragenen Grund —
   * er IST der Grund. Bis heute stand sein Druck darum als
   * „tagesaufnahme-pause-fremd grund=pause-aufruf" im Journal, genau wie
   * eine Pause, die niemand von uns wollte.
   */
  knopfVorMs: number | null = null,
): { anlass: string; zusatz: string } | null {
  if (nachher === vorher) return null;
  if (eigeneGruende.length > 0) return null;
  if (nachher === "paused") {
    const g = (nativerGrund ?? "").replace(/[^a-z0-9-]/gi, "").slice(0, 48);
    // Dasselbe Fenster wie beim eigenen Fortsetzen: so lange nach unserem
    // eigenen Griff gilt ein Wechsel als unserer.
    if (knopfVorMs !== null && knopfVorMs < EIGENES_FORTSETZEN_MS) {
      return {
        anlass: "tagesaufnahme-pause-knopf",
        zusatz: `grund=${g || "unbekannt"} vor=${knopfVorMs}ms`,
      };
    }
    // Martin am Widget / in der Live-Activity: keine fremde Pause, kein Push.
    if (g === "widget") return { anlass: "tagesaufnahme-pause-widget", zusatz: "grund=widget" };
    return { anlass: "tagesaufnahme-pause-fremd", zusatz: `grund=${g || "unbekannt"}` };
  }
  if (nachher === "recording" && vorher === "paused") {
    /*
     * ⚠️ 23.09.2026 (Ticket 2434) — UNSER EIGENES FORTSETZEN STAND ALS FREMD
     *    IM JOURNAL. Wer fortsetzt, trägt sich VORHER aus der Menge aus
     *    (resumeDayRecorderAfterMic), der Zustand springt erst danach um —
     *    die Menge ist in diesem Moment also leer. 7 der 14 „weiter-fremd"
     *    seit pre.3135 kamen 0–3 s nach unserem eigenen
     *    „schonung-fortsetzen". Hat sich eben jemand ausgetragen, war es
     *    unser Fortsetzen.
     */
    if (zuletztAusgetragen && zuletztAusgetragen.vorMs < EIGENES_FORTSETZEN_MS) {
      return {
        anlass: "tagesaufnahme-weiter-eigen",
        zusatz: `grund=${zuletztAusgetragen.grund} vor=${zuletztAusgetragen.vorMs}ms`,
      };
    }
    return { anlass: "tagesaufnahme-weiter-fremd", zusatz: "offen=keine" };
  }
  return null;
}

/**
 * So lange nach dem Austragen gilt ein Fortsetzen als unseres.
 * (23.09.2026, Ticket 2434)
 *
 * Gemessen lagen zwischen „schonung-fortsetzen" und dem falschen
 * „weiter-fremd" 0 bis 3 Sekunden (Tonanlage freiräumen, abgleichen,
 * resume). Fünf Sekunden decken das ab, ohne eine echte fremde Rückkehr
 * Minuten später zu verschlucken.
 */
export const EIGENES_FORTSETZEN_MS = 5_000;

/**
 * Wann Martin zuletzt selbst auf „Pause" gedrückt hat (0 = keine offene).
 * (27.09.2026, Ticket 2708) Gesetzt nur in pauseRecorderForUser, gelesen
 * und verbraucht nur in meldeFremdePause.
 */
let knopfPauseUm = 0;

function meldeFremdePause(s: RecorderState, nativerGrund?: string): void {
  const m = fremdePauseMeldung(
    currentState,
    s,
    [...micPauseGruende],
    zuletztAusgetragen
      ? { grund: zuletztAusgetragen.grund, vorMs: Date.now() - zuletztAusgetragen.um }
      : null,
    nativerGrund,
    knopfPauseUm > 0 ? Date.now() - knopfPauseUm : null,
  );
  if (m) meldeMikroAnlass(m.anlass, m.zusatz);
  /*
   * 27.10.2026 (Ticket 3369): eine fremde Pause im Hintergrund sofort dem
   * Server melden — er schickt „Aufnahme pausiert – tippe, um fortzusetzen".
   * Der Tipp öffnet die App; die Vormerkung setzt dann beim Sichtbarwerden
   * fort, statt auf die Pause-Wache (3 min) zu warten.
   */
  if (meldePauseFuerPush(m?.anlass === "tagesaufnahme-pause-fremd", s, imHintergrund())) {
    merkeFortsetzenVor("pause-push");
  }
  // Ticket 2708 (Nachbesserung): Ein Druck erklärt genau EINE Pause. Blieb
  // er stehen, galt eine fremde Pause kurz nach Knopf-Pause und Fortsetzen
  // (Widget, Engine) ebenfalls als Knopf.
  if (m?.anlass === "tagesaufnahme-pause-knopf") knopfPauseUm = 0;
  if (m?.anlass !== "tagesaufnahme-pause-fremd") return;
  const sitzungMelden = sitzungBeiPauseMelden(Date.now());
  /*
   * 26.09.2026 (Ticket 2615): bei JEDEM fremden Halt die Messwerte der App
   * abholen (sie liegen dort genau einmal je Folge) — die Sitzung selbst nur
   * nach sitzungBeiPauseMelden. Beides in getrennten Zeilen, siehe
   * rekorderMessungZeile.
   */
  void getAudioSessionState({ rekorderMessung: true }).then((st) => {
    if (!st) return;
    const { rekorderMessung, ...sitzung } = st;
    if (sitzungMelden) {
      meldeMikroAnlass("tagesaufnahme-pause-sitzung", JSON.stringify(sitzung).slice(0, 600));
    }
    meldeRekorderMessung(rekorderMessung);
  });
}

/**
 * Die Messwerte der App beim ersten abgelehnten Start einer Folge — als
 * EIGENE Zeile. (26.09.2026, Ticket 2615)
 *
 * Martin: *„Die Aufnahme geht immer auf Pause, wenn das Handy sich
 * sperrt."* Offen ist, welcher Zustand beim Sperren kippt; dafür misst die
 * App (ab Build 87) beim ersten abgelehnten `record()`, wie es um die
 * Tonsitzung steht.
 *
 * ⚠️ DIE ERSTE FASSUNG KAM NIE AN. Die Werte hingen am Sitzungs-JSON von
 *    `tagesaufnahme-pause-sitzung`; der Server behält von jeder Gerätezeile
 *    400 Zeichen (server/index.ts, /api/debug/ptt-timing), Sitzung (~300)
 *    und Messung (~230) passten nicht zusammen. Dazu ist die Reihenfolge
 *    eines Swift-Wörterbuchs zufällig — auf dessen Schnitt war kein Verlass.
 *
 * Darum hier: feste Reihenfolge, `key=value`, jeder Wert gekürzt und
 * bereinigt. Die Grenzen sind so gewählt, dass selbst mit lauter
 * Höchstlängen die ganze Zeile samt Kopf unter 300 Zeichen bleibt.
 */
export const REKORDER_MESSUNG_SCHLUESSEL = [
  "app",
  "seitHinten",
  "sitzungAktiv",
  "kat",
  "eingDa",
  "eingang",
  "rate",
  "kanaele",
  "prepare",
  "ueberlappung",
  "seq",
  "um",
] as const;

/** Höchstlänge je Wert; alle anderen 10 Zeichen. */
const REKORDER_MESSUNG_LAENGE: Readonly<Record<string, number>> = {
  kat: 14, // PlayAndRecord
  eingang: 36, // MicrophoneBuiltIn+BluetoothHFP
  um: 20, // 2026-09-26T12:14:35Z
};

export const REKORDER_MESSUNG_ANLASS = "tagesaufnahme-rekorder-messung";

/** Der Zusatz der Zeile, oder null, wenn die App nichts gemessen hat. */
export function rekorderMessungZeile(m: unknown): string | null {
  if (!m || typeof m !== "object") return null;
  const roh = m as Record<string, unknown>;
  if (!REKORDER_MESSUNG_SCHLUESSEL.some((k) => roh[k] !== undefined)) return null;
  return REKORDER_MESSUNG_SCHLUESSEL.map((k) => {
    const v = roh[k];
    const text = typeof v === "string" || typeof v === "number" || typeof v === "boolean" ? String(v) : "";
    const wert = text.replace(/[^A-Za-z0-9_.:+-]/g, "").slice(0, REKORDER_MESSUNG_LAENGE[k] ?? 10);
    return `${k}=${wert || "-"}`;
  }).join(" ");
}

function meldeRekorderMessung(m: unknown): void {
  const zeile = rekorderMessungZeile(m);
  if (zeile) meldeMikroAnlass(REKORDER_MESSUNG_ANLASS, zeile);
}

/**
 * Wer hält das Mikrofon, wenn die Tagesaufnahme es verliert? (25.09.2026)
 *
 * Martin: *„Die Aufnahme geht immer auf Pause, wenn das Handy sich sperrt.
 * Das hat es vorher nicht gemacht."* Belegt: 12:14:35 gesperrt, sofort
 * „unterbrechung-andere-sitzung", danach 206-mal „mikro-verweigert" bis
 * 12:36:56 — iOS gibt das Mikrofon im Hintergrund nicht neu. WER es nahm,
 * steht nirgends. Darum beim ERSTEN fremden Halt einer Folge den ganzen
 * Zustand der Tonsitzung (Kategorie, Ausgänge, lauscherHaelt, Wiedergabe).
 * Einmal je Folge — die Wiederholungen alle 6 s trügen nichts Neues bei.
 */
export const SITZUNG_BEI_PAUSE_ABSTAND_MS = 120_000;
let sitzungZuletztGemeldet = 0;
let letztePauseFremd = 0;

export function sitzungBeiPauseMelden(jetzt: number): boolean {
  const neueFolge = jetzt - letztePauseFremd > SITZUNG_BEI_PAUSE_ABSTAND_MS;
  letztePauseFremd = jetzt;
  if (!neueFolge && jetzt - sitzungZuletztGemeldet < SITZUNG_BEI_PAUSE_ABSTAND_MS * 10) return false;
  sitzungZuletztGemeldet = jetzt;
  return true;
}

/** Nur für Tests. */
export function _sitzungBeiPauseVergessen(): void {
  sitzungZuletztGemeldet = 0;
  letztePauseFremd = 0;
}

function notifyState(s: RecorderState, nativerGrund?: string): void {
  /*
   * ⚠️ VOR der Zuweisung an currentState — die Meldung braucht den ALTEN
   *    Zustand, um einen Wechsel von einer Wiederholung zu unterscheiden.
   */
  meldeFremdePause(s, nativerGrund);
  if (s === "recording") merkeLaeuft();
  if (s === "paused") meldePauseGrund(nativerGrund);
  gleicheMitZustandAb(s, false);
  // Frischer Start (idle/finishing → recording) setzt den Zeitstempel; ein
  // resume (paused → recording) behält ihn; idle setzt zurück.
  if (
    s === "recording" &&
    currentState !== "recording" &&
    currentState !== "paused"
  ) {
    recordingStartedAt = Date.now();
    lastUploadInfo = { pending: 0, uploaded: 0 };
  } else if (s === "idle") {
    recordingStartedAt = 0;
    lastUploadInfo = { pending: 0, uploaded: 0 };
  }
  currentState = s;
  emitStateEvent(s);
  activeCb.onState?.(s);
  for (const f of globalObservers) {
    try {
      f(s);
    } catch {
      /* Beobachter-Fehler nicht durchreichen */
    }
  }
}

/** True nur in der nativen Hülle MIT verfügbarem Recorder-Plugin. */
export function isRecorderAvailable(): boolean {
  return isNativePlatform() && !!getRecorder();
}

/**
 * Laesst sich HIER aufzeichnen? (22.09.2026)
 *
 * Martin: *„Uebrigens habe ich noch immer nicht entdeckt, wo man im Browser
 * auf der Webseite auf ‚Rekorden‘ druecken kann. Das funktioniert so, wie es
 * ausschaut, noch nicht. Meinen Alltag aufnehmen ueber den Browser."*
 *
 * ⚠️⚠️ ER HAT NICHTS UEBERSEHEN — DEN KNOPF GAB ES NICHT.
 *
 * Seit dem 17.09. faellt `startRecording()` im Browser auf den
 * BrowserRecorder zurueck, und seit heute haelt auch `stopRecording()` ihn
 * an. Die Aufzeichnung LAEUFT dort also. Nur entschied die Oberflaeche
 * weiterhin mit `isRecorderAvailable()`, ob sie ueberhaupt einen Knopf
 * zeichnet — und das heisst „native Huelle da", nicht „Aufnahme moeglich".
 * Die Faehigkeit war fertig gebaut und blieb unerreichbar.
 *
 * Dasselbe Muster wie am 16.09. dreimal an einem Tag (analyze_video, die
 * Zuhause-Zeile, der Stopp-Knopf): erst messen, ob der Klick ankommt.
 *
 * ⚠️ Die Trennung bleibt bestehen. `isRecorderAvailable()` heisst weiterhin
 *    „das native Plugin ist da" und gehoert ueberall dorthin, wo es wirklich
 *    um die Huelle geht — Widget-Auffrischung, native Warteschlange,
 *    Zustandsabgleich. Diese Funktion beantwortet die andere Frage: darf ich
 *    dem Menschen eine Aufnahme ANBIETEN?
 */
export function kannAufzeichnen(): boolean {
  if (isRecorderAvailable()) return true;
  return typeof navigator !== "undefined" && Boolean(navigator.mediaDevices);
}

/**
 * Seit wann die Sprechtaste über die native Engine aufnimmt — 0, wenn nicht.
 * (24.09.2026, Ticket 2202)
 *
 * ⚠️⚠️ DIESE LÜCKE HAT DEN NATIVEN WEG SCHON EINMAL GEKOSTET.
 *
 * Der Web-Weg trägt sich als Pausengrund „ptt" ein; die Rettung und die
 * Wachen sehen ihn und warten. Der native Weg pausierte am 08.09. nichts —
 * also sahen sie ihn auch nicht. Um 16:07 lief eine native Aufnahme, und fünf
 * Sekunden später riss die Rettung die Tagessitzung ab
 * (`fortsetzen-gescheitert:neue-sitzung`) und nahm Martin das Mikro mitten
 * im Satz (siehe micAnmeldungen).
 *
 * Solange die Probe den Weg wieder öffnet, zählt er darum wie ein Grund:
 * `hatOffenePausenGruende()` und `fremdeHand()` fragen danach.
 *
 * ⚠️ Seit dem zweiten Bau (24.09.2026) trägt auch die native Sprechtaste
 *    den Grund „ptt" ein — aber erst NACH ihrem Start (ptt.ts,
 *    halteTagesaufnahmeAn). Diese Marke deckt das, was davor und daneben
 *    liegt: die Brücke zwischen Start und Anhalten, ein Anhalten, das hängt
 *    oder nichts anzuhalten fand. Sie sagt „der zweite Rekorder läuft", der
 *    Grund sagt „wer hat angehalten und setzt fort" — zwei Tatsachen, nicht
 *    zwei Besitzer derselben.
 *
 * ⚠️ Mit Deckel: Eine Aufnahme dauert höchstens PTT_MAX_MS (25 min,
 *    ptt.ts). Bleibt die Marke länger stehen, ist ein Ende verloren
 *    gegangen — dann darf sie die Rettung nicht für immer schlafen legen.
 */
let pttNativSeit = 0;
export const PTT_NATIV_DECKEL_MS = 26 * 60_000; // 25.09.2026: PTT_MAX_MS 25 min + 1 (Ticket 2618)

/** Nimmt die Sprechtaste gerade über die native Engine auf? */
export function pttNativHaelt(jetzt = Date.now()): boolean {
  return pttNativSeit > 0 && jetzt - pttNativSeit < PTT_NATIV_DECKEL_MS;
}

/** Aktueller Recorder-Zustand (für die UI). */
/**
 * PTT aus der nativen Engine. (05.09.2026)
 *
 * Gibt die drei Griffe zurück, wenn die Hülle sie kennt — sonst null, und
 * ptt.ts nimmt den alten Weg. Warum: Der Mikro-Start über getUserMedia
 * kostet auf Bluetooth 1,5–4,5 s (Profilwechsel A2DP → HFP) und hinterlässt
 * den Telefonklang; die Tagesaufnahme hat das Mikro längst offen.
 */
export function holePttNativ(): {
  start(): Promise<void>;
  stop(): Promise<{ base64: string; mime?: string }>;
  cancel(): Promise<void>;
  /** Pegel 0…1 für die Balken — 0, wenn die Hülle ihn nicht kennt. */
  pegel(): Promise<number>;
} | null {
  const plugin = getRecorder();
  if (!plugin?.pttStart || !plugin.pttStop || !plugin.pttCancel) return null;
  return {
    start: async () => {
      /*
       * Ticket 2202: ab jetzt spricht jemand über das Mikro der Tagesaufnahme.
       *
       * ⚠️ Die Anmeldung zählt VOR dem Aufruf — eine Rettung, die gerade
       *    läuft, tritt dann schon während der Brücke zurück. Die Marke
       *    selbst erst NACH dem Erfolg: hinge der Aufruf, hielte sie sonst
       *    bis zum Deckel, und so lange schliefe jede Rettung.
       */
      micAnmeldungen++;
      await plugin.pttStart!();
      pttNativSeit = Date.now();
    },
    // ⚠️ `finally`: auch ein gescheitertes Ende gibt die Sperre frei — sonst
    //    hielte sie bis zum Deckel und die Rettung schliefe so lange.
    stop: async () => {
      try {
        return await plugin.pttStop!();
      } finally {
        pttNativSeit = 0;
      }
    },
    cancel: async () => {
      try {
        await plugin.pttCancel!();
      } finally {
        pttNativSeit = 0;
      }
    },
    pegel: async () => {
      if (!plugin.pttLevel) return 0;
      const r = await plugin.pttLevel();
      return typeof r?.level === "number" ? r.level : 0;
    },
  };
}

export function getRecorderState(): RecorderState {
  return currentState;
}

/** Zeitstempel (ms) des laufenden Aufnahme-Starts; 0 wenn keine Aufnahme läuft. */
export function getRecordingStartedAt(): number {
  return recordingStartedAt;
}

/** 2026-07-24 (Martin): laufende Aufnahmedauer als mm:ss (bzw. h:mm:ss).
 *  Leerer String, wenn keine Aufnahme laeuft. */
export function formatRecordingElapsed(): string {
  if (!recordingStartedAt) return "";
  const total = Math.max(0, Math.floor((Date.now() - recordingStartedAt) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  const zwei = (n: number): string => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${zwei(m)}:${zwei(sec)}` : `${m}:${zwei(sec)}`;
}

/** Zeitstempel (ms) des Pause-Beginns; 0 wenn nicht pausiert (oder alte Hülle). */
export function getRecordingPausedAt(): number {
  return recordingPausedAt;
}

/** Letzter Upload-Stand: offene + hochgeladene Häppchen der laufenden/letzten
 * Aufnahme. pending > 0 = wird noch übertragen (ggf. Flugmodus → wartet aufs Netz). */
export function getUploadInfo(): { pending: number; uploaded: number } {
  return lastUploadInfo;
}

/**
 * An eine BEREITS LAUFENDE Aufnahme anhängen (Listener neu verdrahten), OHNE neu
 * zu starten — z.B. wenn das Aufnahme-Overlay erneut geöffnet wird. Verhindert,
 * dass ein zweiter „Aufnehmen"-Tipp die laufende Aufnahme neu startet.
 */
// 2026-07-04 (#35): dauerhafter Status-Kanal — wird nie verdraengt.
export function observeRecorderGlobal(cb: (s: RecorderState) => void): void {
  globalObservers.add(cb);
}

export async function observeRecorder(
  cb: RecorderCallbacks = {},
): Promise<void> {
  const plugin = getRecorder();
  if (!plugin) return;
  await attach(plugin, cb);
}

/**
 * Den JS-Zustand mit dem ECHTEN nativen Recorder abgleichen. Nach einem App-/
 * WebView-Reload läuft eine Aufnahme evtl. schon im Hintergrund weiter, während
 * der JS-Adapter „idle" annähme → falsche Uhr (0) / versehentlicher Neustart /
 * Stopp wirkt nicht. Vor dem Öffnen des Overlays aufrufen.
 */
export async function syncFromNative(): Promise<void> {
  const plugin = getRecorder();
  if (!plugin) return;
  try {
    const s = await plugin.getState();
    if (s?.state && VALID_STATES.has(s.state)) {
      // 2026-07-14 (Martin, Zombie-Aufnahme "222s nicht gesichert"): weicht
      // der ECHTE native Zustand vom JS-Glauben ab (z.B. optimistisches
      // "recording", Engine startete aber nie), auch das AKTIVE Overlay
      // informieren — vorher blieb es fuer immer auf dem falschen Zustand
      // (Stopp-Knopf tat scheinbar nichts).
      const changed = currentState !== s.state;
      currentState = s.state as RecorderState;
      if (currentState === "recording") merkeLaeuft();
      gleicheMitZustandAb(currentState);
      if (currentState === "paused") {
        meldePauseGrund(s.grund);
        meldePausenStart(typeof s.pausedAt === "number" ? s.pausedAt * 1000 : 0, s.grund);
      }
      emitStateEvent(currentState);
      if (changed) {
        try {
          activeCb.onState?.(currentState);
        } catch {
          /* Overlay evtl. weg */
        }
      }
    }
    recordingStartedAt =
      typeof s?.startedAt === "number" && s.startedAt > 0
        ? s.startedAt * 1000
        : 0;
    recordingPausedAt =
      typeof s?.pausedAt === "number" && s.pausedAt > 0
        ? s.pausedAt * 1000
        : 0;
    if (typeof s?.dayIntent === "boolean") dayIntentActive = s.dayIntent;
    // Die Absicht zaehlt fuer die Tonanlage genauso wie der Zustand: in der
    // Luecke zwischen zwei Sitzungen darf die Session nicht auf .playback
    // fallen, sonst findet der Neustart kein Mikrofon.
    setAlltagNimmtAuf(
      currentState === "recording" ||
        currentState === "paused" ||
        currentState === "finishing" ||
        dayIntentActive,
    );
    // Ticket 2438 (23.09.2026): der Server erfährt den Start, nicht erst
    // das erste Häppchen — hier, weil nur der Abgleich den ECHTEN Beginn kennt.
    meldeAufnahmeLauf({ laeuft: isAlltagRecorderLive(), startedAt: recordingStartedAt });
  } catch {
    /* getState nicht verfügbar (alte Hülle) → Zustand unverändert */
  }
  for (const f of globalObservers) {
    try {
      f(currentState);
    } catch {
      /* ignore */
    }
  }
}

async function detach(): Promise<void> {
  for (const h of listeners) {
    try {
      h.remove();
    } catch {
      /* Handle evtl. schon weg */
    }
  }
  listeners = [];
}

async function attach(
  plugin: PawbotRecorderPlugin,
  cb: RecorderCallbacks,
): Promise<void> {
  await detach();
  activeCb = cb;
  const add = async (
    event: "recorderState" | "uploadProgress" | "error",
    fn: (d: unknown) => void,
  ): Promise<void> => {
    listeners.push(await plugin.addListener(event, fn));
  };
  await add("recorderState", (d) => {
    const payload = d as { state?: string; dayIntent?: boolean; grund?: string } | undefined;
    if (typeof payload?.dayIntent === "boolean") {
      dayIntentActive = payload.dayIntent;
    }
    const s = payload?.state;
    if (s && VALID_STATES.has(s)) {
      notifyState(s as RecorderState, typeof payload?.grund === "string" ? payload.grund : undefined);
      // Terminaler „idle"-Event = Aufnahme fertig → erst MELDEN, dann Listener lösen.
      // (NICHT in stopRecording detachen — sonst käme der idle-Event nach dem Lösen
      // an und die UI bliebe auf „finishing" hängen.)
      // #35: NICHT detach() — die Plugin-Lauscher bleiben, damit auch eine
    // per WIDGET gestartete naechste Aufnahme JS-seitig gehoert wird. Nur
    // die interaktiven Overlay-Callbacks loslassen.
    if (s === "idle") activeCb = {};
    }
  });
  await add("uploadProgress", (d) => {
    const p = d as { pending?: number; uploaded?: number } | undefined;
    lastUploadInfo = {
      pending: typeof p?.pending === "number" ? p.pending : 0,
      uploaded: typeof p?.uploaded === "number" ? p.uploaded : 0,
    };
    lastUploadAt = Date.now();
    activeCb.onUploadProgress?.(lastUploadInfo);
  });
  await add("error", (d) => {
    const msg = (d as { message?: string } | undefined)?.message;
    activeCb.onError?.(new Error(msg ?? "recorder error"));
  });
}

// 2026-07-20 (Martin #379): "Immer aufzeichnen" — rund um die Uhr.
// Setzt die native Absicht (App-Group) und startet/stoppt sofort.
export async function setAlwaysDayRecording(active: boolean): Promise<boolean> {
  const plugin = getRecorder();
  if (!plugin?.setAlwaysRecord) return false;
  try {
    const r = await plugin.setAlwaysRecord({ active });
    return r?.active === true;
  } catch {
    return false;
  }
}

export function isAlwaysRecordSupported(): boolean {
  return !!getRecorder()?.setAlwaysRecord;
}

/** 2026-07-23: Laeuft die Aufnahme unter dem "Immer aufzeichnen"-Intent?
 *  (Nativ gemeldet ueber die State-Syncs — Basis fuer den Stopp-Hinweis.) */
export function isDayIntentActive(): boolean {
  return dayIntentActive;
}

/** Alltag-Rekorder lebt: aufnehmen, pausiert, oder Tages-Absicht steht.
 *  Sichtbarkeit/Warm-Mikro duerfen in diesem Zustand die Session nicht
 *  auf .playback legen und kein neues getUserMedia oeffnen. */
export function isAlltagRecorderLive(): boolean {
  return (
    currentState === "recording" ||
    currentState === "paused" ||
    currentState === "finishing" ||
    dayIntentActive
  );
}

/**
 * Auf das native Plugin warten.
 *
 * 2026-08-23 (Martin: "Wenn ich auf Aufnehmen klicke, beginnt oben im Widget
 * 'Aufnehmen', und es laeuft augenscheinlich, aber in der Notch sehe ich nicht
 * das Aufnahme-Symbol. Nichts passiert. Erst wenn ich noch mal draufklicke,
 * faengt die Aufnahme zum Laufen an."):
 *
 *   Der Widget-Knopf ist ein Deep-Link — er OEFFNET die App. Beim Kaltstart ist
 *   Capacitor noch dabei, seine Plugins zu registrieren, wenn der Deep-Link
 *   schon ankommt. getRecorder() lieferte dann null, startRecording() kehrte
 *   still zurueck, und das Overlay zeigte trotzdem "Nimmt auf" mit laufender
 *   Uhr. Beim zweiten Tippen lief die App bereits — deshalb ging es dann.
 *
 *   Die verlorene Zeit ist genau die Spanne zwischen den beiden Klicks: Martin
 *   sprach in ein Mikro, das nie an war.
 *
 * Eine Sekunde reicht fuer den Kaltstart; laenger zu warten hiesse, einen
 * echten Ausfall zu verschleppen.
 */
async function warteAufRecorder(maxMs = 1000): Promise<PawbotRecorderPlugin | null> {
  const bis = Date.now() + maxMs;
  for (;;) {
    const p = getRecorder();
    if (p) return p;
    if (Date.now() >= bis) return null;
    await new Promise((r) => setTimeout(r, 50));
  }
}

export async function startRecording(
  cb: RecorderCallbacks = {},
): Promise<void> {
  /*
   * Jeder Start nimmt ein frueheres bewusstes Aus zurueck — an EINER Stelle,
   * damit kein Aufrufer es vergessen kann. Die Wache ruft diese Funktion nur,
   * wenn gar kein Aus gesetzt ist; sie kann sich also nicht selbst freigeben.
   */
  nimmAusZurueck();
  const plugin = await warteAufRecorder();
  if (!plugin) {
    /*
     * ⚠️⚠️ 17.09.2026 — OHNE NATIVE HUELLE IST JETZT NICHT SCHLUSS.
     *
     * Martin: *„Es gibt ja meinen Alltag, das geht ja nur mit der nativen
     * App. Koennte man die PWA aufpimpen, dass Recording auch geht … Albert
     * sitzt die meiste Zeit am Laptop und da koennte er es laufen lassen und
     * nebenbei arbeiten."*
     *
     * Hier stand `notifyState("error")` — im Browser war die Daueraufnahme
     * damit schlicht nicht vorhanden. Sie IST im Browser moeglich, nur mit
     * einer Grenze, die iOS setzt (siehe browser-recorder.ts): dort endet
     * sie beim Sperren des Bildschirms.
     *
     * ⚠️ Der Rueckfall nimmt DIESELBE Schnittstelle zum Server
     *    (/api/recorder/chunk, /api/recorder/finish). Das Ergebnis landet
     *    im selben „Mein Alltag", mit derselben Verarbeitung. Ein zweiter
     *    Weg waere eine zweite Wahrheit.
     */
    const browser = await starteImBrowser(cb);
    if (browser) return;
    // Auch das ging nicht (kein Mikrofon, verweigert) — dann ist es ein
    // echter Ausfall, und die Oberflaeche muss es erfahren.
    meldeMikroAnlass("tagesaufnahme-start-fehler", "kein-rekorder");
    notifyState("error");
    cb.onError?.(new Error("Keine Aufnahme möglich (kein Mikrofon)"));
    return;
  }
  await attach(plugin, cb);
  // 2026-07-14 (Martin, "Aufnahme starb nach 8.8s Audio"): das PTT-Warm-
  // Mikro VOR dem nativen Start freigeben und WebKit ~450ms zur Ruhe
  // kommen lassen. Die bisherige Freigabe NACH dem Start riss die
  // Audio-Session mitten im Anlauf um -> wiederholte Unterbrechungen,
  // Engine gab nach ~20s auf und schloss die Session ab.
  try {
    const detail: { held?: boolean } = {};
    window.dispatchEvent(
      new CustomEvent("voice-v8:recorder-will-start", { detail }),
    );
    if (detail.held) await new Promise((r) => setTimeout(r, 450));
  } catch {
    /* kein window (Tests) */
  }
  /*
   * 2026-08-23: notifyState("recording") stand hier VOR dem Start. Damit sagte
   * die App "es laeuft", bevor das Mikro ueberhaupt angefordert war — Widget,
   * Uhr und Statuszeile zeigten eine Aufnahme, die es noch nicht gab.
   *
   * Jetzt erst melden, wenn der native Recorder den Start angenommen hat.
   * Scheitert er, war der Zustand nie "recording" und niemand hat sich auf
   * eine Aufnahme verlassen, die keine war.
   */
  try {
    await plugin.start({ maxDurationSec: DEFAULT_MAX_DURATION_SEC });
    notifyState("recording");
  } catch (err) {
    /*
     * 23.09.2026 — Martin: „Das Live-Recording aktiv schalten funktioniert
     * nicht." Ein gescheiterter Start hinterliess KEINE Zeile: die Zuhause-
     * Karte gab keinen onError mit, und das Server-Protokoll kennt nur
     * Sitzungen, die zustande kamen. Seit Build 66 (22.09., 23:31) gab es
     * keine einzige — ob es Versuche gab, war nicht zu sagen.
     */
    const grund = err instanceof Error ? err.message : String(err);
    meldeMikroAnlass("tagesaufnahme-start-fehler", grund.slice(0, 120).replace(/\s+/g, "-"));
    notifyState("error");
    cb.onError?.(err instanceof Error ? err : new Error(String(err)));
    await detach();
  }
}

/**
 * Die Tages-Aufnahme beenden.
 *
 * ⚠️⚠️ 17.09.2026 — JEDER Aufruf nennt seinen Verursacher.
 *
 * Martin: *„Schon wieder Pause das gibt es nicht!!! Ich nehme die ganze Zeit
 * einen wertvollen Kurs auf."* Vierzehn Minuten Kursmaterial fehlten.
 *
 * Im Server-Protokoll stand nur `endReason=stopp` — und das ist der
 * VORGABEWERT der nativen `stop()`. Ihn tragen der Knopf in der App, der
 * Stopp im Sperrbildschirm-Widget und jeder interne Aufruf gleichermassen.
 * Eine Stunde Suche, und am Ende blieben drei Verdaechtige, zwischen denen
 * die Daten nicht unterscheiden konnten.
 *
 * Deshalb steht der Anlass jetzt VOR dem Stopp im Protokoll. Der naechste
 * Vorfall ist damit in Sekunden benannt statt in einer Stunde.
 */
export async function stopRecording(anlass = "unbenannt"): Promise<void> {
  const plugin = getRecorder();
  if (!plugin) {
    /*
     * ⚠️⚠️ 22.09.2026 — IM BROWSER HIELT DER STOPP-KNOPF NICHTS AN.
     *
     * Hier stand ein blankes `return`. Der Browser-Rekorder lebt aber in
     * einer eigenen Variablen (`browserRekorder`) und hat ein `stop()` —
     * er lief einfach weiter, und `notifyState("idle")` kam nie.
     *
     * Der einzige Weg zu „idle" war `beiWechsel`, und der feuert nur bei
     * einem Tab-Wechsel. Zwei Folgen, beide gemessen im Bericht:
     *
     *   1. Wer im Browser auf Stopp drueckt, stoppt nichts. Die Aufnahme
     *      laeuft weiter, bis der Reiter weggeschaltet wird.
     *   2. `dayRecorderBusy` bleibt fuer die Lebensdauer der Seite auf
     *      „belegt" — der Vorwaermer waere dort dauerhaft aus, und die
     *      ganze Arbeit an ihm braechte am Laptop nichts.
     */
    if (browserRekorder) {
      meldeMikroAnlass(`tagesaufnahme-stopp:${anlass}`);
      console.warn(
        `[recorder] Browser-Aufnahme wird beendet — Anlass: ${anlass}`,
      );
      notifyState("finishing");
      try {
        await browserRekorder.stop(anlass);
      } catch (e) {
        console.warn("[recorder] Browser-Stopp fehlgeschlagen:", e);
      }
      browserRekorder = null;
      notifyState("idle");
    }
    return;
  }
  meldeMikroAnlass(`tagesaufnahme-stopp:${anlass}`);
  console.warn(`[recorder] Tagesaufnahme wird beendet — Anlass: ${anlass}`);
  notifyState("finishing");
  // Zombie-Schutz: meldet die Engine kein idle (weil sie den Stopp
  // still verweigerte — sie war nie am Aufnehmen), nach 8s die Wahrheit
  // vom nativen Recorder holen, damit die UI nicht haengen bleibt.
  window.setTimeout(() => {
    if (getRecorderState() === "finishing") void syncFromNative();
  }, 8_000);
  try {
    await plugin.stop({ nutzer: anlass === "knopf-in-der-app", grund: anlass });
  } catch {
    /* stop-Fehler ignorieren — der Endzustand kommt via recorderState-Event */
  }
  // KEIN detach() hier: der terminale „idle"-Event (siehe attach) meldet den
  // Abschluss an die UI und löst DANN die Listener. Würde man hier lösen, käme
  // das idle-Event nach dem Lösen an → UI bliebe auf „finishing" hängen.
}

/**
 * Bewusster Stopp (Widget, Overlay, Liste): Sitzung beenden UND die
 * Durchgehend-Absicht löschen. `stopRecording()` allein lässt die native
 * Engine neu anlaufen — Header und Notch sagen dann „steht", das Overlay
 * „läuft". Der Schalter in den Einstellungen bleibt unberührt.
 *
 * Gibt zurück, ob die Absicht vorher an war (Hinweis in der Oberfläche).
 */
/**
 * Merker fuer ein BEWUSSTES Aus. (06.09.2026)
 *
 * Der Schalter in den Einstellungen bleibt bei einem Stopp absichtlich
 * unberuehrt (Entscheidung oben). Seit es die Wache in `aufnahme-wache.ts`
 * gibt, reicht das nicht mehr: Sie liest genau diesen Schalter und wuerde
 * eine von Hand beendete Aufnahme binnen einer Minute wieder anwerfen.
 *
 * Also ein zweiter, ausdruecklicher Merker: Wer stoppt, hat gestoppt. Er
 * faellt weg, sobald jemand wieder von Hand beginnt oder „Durchgehend"
 * einschaltet — beides sind Handlungen, die das Aus zuruecknehmen.
 */
export const AUFNAHME_AUS_KEY = "voice-v8:aufnahme-aus-seit";

/** Hat der Nutzer die Aufnahme selbst beendet? */
export function bewusstBeendet(): boolean {
  try {
    return localStorage.getItem(AUFNAHME_AUS_KEY) !== null;
  } catch {
    return false;
  }
}

/** Das Aus zuruecknehmen — bei jedem Start von Hand. */
export function nimmAusZurueck(): void {
  try {
    localStorage.removeItem(AUFNAHME_AUS_KEY);
  } catch {
    /* egal */
  }
}

export async function stopRecordingForUser(): Promise<boolean> {
  const warDauer = dayIntentActive;
  try {
    localStorage.setItem(AUFNAHME_AUS_KEY, String(Date.now()));
  } catch {
    /* Quota/Privat-Modus — dann greift nur die native Absicht */
  }
  await stopRecording("knopf-in-der-app");
  await setAlwaysDayRecording(false);
  await syncFromNative();
  return warDauer;
}

/** Aufnahme/Pause umschalten. */
/**
 * Pause umschalten — auf dem ECHTEN Zustand, nicht auf dem gemerkten.
 *
 * ⚠️ 05.09.2026, Martin mit Bild: *„Kann nach Pause nicht mehr auf
 *    weiterklicken."* Die Aufnahme stand auf 5:22, der Knopf tat nichts.
 *
 *    Hier wurde `currentState` gelesen — und der wird nur alle 15 Sekunden
 *    abgeglichen. Stand dort etwas anderes als „paused" (etwa noch
 *    „recording" von vor der Pause), fiel die Funktion durch BEIDE Zweige
 *    und tat schweigend gar nichts.
 *
 *    Genau diese Lehre steht seit dem 25.07. zweimal in dieser Datei — bei
 *    `pauseDayRecorderForMic` und bei `resumeDayRecorderAfterMic`
 *    („NICHT mehr dem zwischengespeicherten Zustand glauben"). Der
 *    Umschalter hatte sie nie bekommen.
 *
 * @param nutzerwunsch Kam der Wunsch von Martin selbst (Knopf)? Dann
 *   räumen wir alle Pausengründe: Wer drückt, will fortsetzen — egal,
 *   was das System für offen hält.
 */
/**
 * Programmatisch anhalten — ohne zu kippen. (05.09.2026)
 *
 * Bis heute rief jeder programmatische Pfad `toggleRecorderPause()`. Der
 * kippt seit dem 04.09. den ECHTEN Zustand — richtig für den Knopf, fatal
 * für Code, der mit gemerktem (bis 15 s altem) Wissen „fortsetzt": Hatte
 * ein anderer schon fortgesetzt, kippte der zweite die laufende Aufnahme
 * in Pause. Martin: „geht immer noch auf Pause, obwohl nichts rennt."
 * Diese zwei Verben sind idempotent: sie tun nur, was noch aussteht.
 */
/**
 * Die Aufnahme jetzt anhalten.
 *
 * @returns ob wirklich etwas angehalten wurde.
 *
 * ## ⚠️⚠️ 22.09.2026 — IM BROWSER HIELT HIER NICHTS AN, UND NIEMAND ERFUHR ES
 *
 * `getRecorder()` ist im Browser null — die Funktion kehrte sofort zurueck.
 * Der Aufrufer `pauseDayRecorderForMic` lief trotzdem weiter, trug den
 * Grund ein, schrieb den Marker und meldete **`true`**: Erfolg.
 *
 * Der Browser-Rekorder (`browser-recorder.ts`) hat gar kein `pause()`. Er
 * kann nur `getZustand()` und `beiHintergrund()`. Es gibt dort also keine
 * Pause — und ein Schutz, der auf dieser Zusage aufbaut, ist ein
 * Schein-Schutz.
 *
 * Gefunden von der unabhaengigen Pruefung zum Browser-Vorwaermer: *„Wer den
 * Vorwaermer im Browser absichern will, darf sich NICHT auf
 * `pauseDayRecorderForMic` verlassen — sie gibt true zurueck, ohne etwas
 * anzuhalten."*
 */
async function halteJetztAn(): Promise<boolean> {
  const plugin = getRecorder();
  if (!plugin) {
    /*
     * Kein natives Plugin. Laeuft hier eine BROWSER-Aufnahme, koennen wir
     * sie nicht anhalten — dann ist das eine Auskunft und kein Nichts.
     */
    if (laeuftImBrowser()) {
      console.warn(
        "[recorder] Browser-Aufnahme laeuft und kann nicht pausiert werden " +
          "(der Browser-Rekorder kennt keine Pause).",
      );
      return false;
    }
    return true;
  }
  await syncFromNative().catch(() => undefined);
  if (getRecorderState() === "recording") await plugin.pause();
  return true;
}

/**
 * Martin drueckt auf Aufnahme, waehrend etwas spielt. (15.09.2026)
 *
 * *„Ich kann auch da nicht auf Rekord schalten. Weder beim Widget noch in
 * der Seite selbst."* — weil auf iOS die Wiedergabe-Kategorie das Mikrofon
 * haelt: Solange der Spiegel laeuft, kann kein Rekorder starten oder
 * fortsetzen, und der Druck verpufft still. Wer auf Aufnahme drueckt, will
 * aufnehmen — die Wiedergabe tritt zurueck. Erst die Buehne anhalten, die
 * Halte loesen, dem Geraet den Wechsel bestaetigen lassen, DANN der Griff.
 */
export async function raeumeTonFuerAufnahme(): Promise<void> {
  try {
    const { aktuellerTon, halteAn } = await import("./ton-leitstand.js");
    if (aktuellerTon()?.klingt) halteAn();
  } catch {
    /* ohne Leitstand: nichts anzuhalten */
  }
  loeseWiedergabeHalt("nutzer-will-aufnehmen");
  // Den nativen Wechsel abwarten — ein resume() in der Wiedergabe-Kategorie
  // kehrt stumm zurueck (siehe setzeJetztFort).
  await setPlaybackHiFi(false).catch(() => null);
}

/**
 * Hält ein Sprachanruf das Mikrofon? (02.10.2026, Ticket 2728)
 *
 * Vespera rief an, Martin hob ab, das Gespräch stand — und 14 s später
 * versuchte die Tagesaufnahme sechsmal fortzusetzen (`fortsetzen-versuch-1…6`,
 * jede Zeile mit `callActive:true`), riss dann die Sitzung ab und begann neu
 * (`rettung-nach-gescheitertem-fortsetzen`), mitten im Anruf. Beim Anruf über
 * CallKit hat iOS die Aufnahme schon beim KLINGELN angehalten; beim Abheben
 * fand `pauseDayRecorderForMic("anruf")` sie darum „pausiert ohne Grund" und
 * trug keinen ein. Für die Wachen sah das aus wie eine hängende Pause.
 *
 * Nativ gilt längst: neben einem Anruf startet und setzt nichts fort
 * (`Tonbelegung.aktiv`), und nach dem Ende setzt die Engine selbst fort
 * (`tagesaufnahme-weiter-eigen grund=anruf`). Die Seite fragt darum die App
 * (`callActive`), bevor sie anstößt oder abreißt. Ohne Antwort (Browser,
 * alte App): wie bisher.
 */
async function anrufHaeltMikro(): Promise<boolean> {
  const st = await getAudioSessionState();
  return st?.callActive === true;
}

async function setzeJetztFort(): Promise<void> {
  const plugin = getRecorder();
  if (!plugin) return;
  /*
   * ⚠️ Eine Pause von Martin hebt KEIN automatischer Weg auf (Wache,
   *    Wiedergabe-Ende, Selbstheilung). Sein eigenes Fortsetzen loescht den
   *    Eintrag vorher (resumeRecorderForUser).
   */
  if (istNutzerPause()) {
    meldeMikroAnlass("fortsetzen-verweigert:nutzer-pause");
    return;
  }
  if (await anrufHaeltMikro()) {
    meldeMikroAnlass("fortsetzen-aufgeschoben:anruf");
    return;
  }
  /*
   * ⚠️ 07.09.2026 — ZUERST die Tonanlage freiraeumen, DANN fortsetzen.
   *
   * Auf iOS gibt die Kategorie `.playback` das Mikrofon frei; in ihr kann
   * kein Rekorder fortsetzen. Am 07.09. um 06:49:56 stand genau das im
   * Protokoll — und danach kein `playback-end` mehr. Die Aufnahme blieb
   * pausiert, und zwar fuer JEDEN Weg: Knopf im Zuhause, nativer Knopf im
   * Blatt, Wiederholer. Martin musste die App neu starten.
   */
  loeseWiedergabeHalt("aufnahme-setzt-fort");
  await syncFromNative().catch(() => undefined);
  if (getRecorderState() === "paused") await plugin.resume();
  void bestaetigeFortsetzung();
}

/**
 * Muss die Wache warten, weil die Engine das Fortsetzen ohnehin ablehnt?
 * (Anruf; Vespera spricht — natives resume() lehnt ab, solange die
 * Tonbelegung aktiv ist.) Das ist KEIN Fehlschlag und kein Alarm.
 */
export async function wacheMussWarten(): Promise<boolean> {
  if (await anrufHaeltMikro()) return true;
  if (leseNativenGrund() !== "wiedergabe") return false;
  try {
    const w = wiedergabeHaltStand();
    if (w.spielend > 0 || w.hiFi || w.vorwaermung) return true;
  } catch {
    /* ohne Stand: nicht warten */
  }
  return etwasKlingt();
}

/**
 * Der Weg der Wache: fortsetzen, ohne die Rueckkehr-Funktion der Gruende.
 * Gibt false zurueck, wenn die Pause Martin gehoert oder nichts pausiert ist.
 */
export async function wacheSetztFort(): Promise<boolean> {
  if (urteilFuerWache() !== "belegt") return false;
  try {
    localStorage.removeItem(MIC_PAUSE_KEY);
  } catch {
    /* egal */
  }
  await setzeJetztFort();
  await syncFromNative().catch(() => undefined);
  return getRecorderState() === "recording";
}

/**
 * Steht der Schalter „durchgehend aufnehmen" auf an?
 *
 * ⚠️ Aus dem Speicher gelesen, nicht aus der nativen Absicht — die ist an
 *    dieser Stelle gerade geloescht worden. Dieselbe Quelle wie in
 *    aufnahme-wache.ts; der Schalter ist die Absicht des Nutzers, die
 *    native Absicht nur ihre Umsetzung.
 */
function dauerGewuenscht(): boolean {
  try {
    return localStorage.getItem("voice-v8.alwaysRecord") === "1";
  } catch {
    return false;
  }
}

/**
 * Wie lange dem Fortsetzen Zeit gegeben wird, bevor die Sitzung als tot gilt.
 *
 * ⚠️ 08.09.2026 — von 3 × 1,5 s auf 6 × 2,5 s. Martin: *„Die Aufnahme reißt
 *    schon wieder immer wieder ab!"*
 *
 *    Gemessen im Journal: am 06.09. endeten ZWEI Aufnahmen am ganzen Tag, am
 *    07.09. FÜNFZEHN — und am 07.09. um 07:09 ging die Rettung unten live
 *    (pre.2676). Sie hat aus „eine Pause klemmt" viele zerrissene Sitzungen
 *    gemacht: Jeder gescheiterte Anlauf schliesst seither die Sitzung und
 *    beginnt eine neue, und genau das spuert er als Abreissen.
 *
 *    Viereinhalb Sekunden sind zu knapp fuer eine iOS-Tonsitzung, die gerade
 *    umschaltet (Bluetooth-Profilwechsel allein dauert bis zu vier). Fuenfzehn
 *    Sekunden Geduld kosten nichts — die Aufnahme laeuft ja weiter, sobald
 *    das Mikro frei wird.
 */
const FORTSETZ_VERSUCHE = 6;
const FORTSETZ_TAKT_MS = 2500;

/**
 * Klingt gerade etwas?
 *
 * ⚠️ Die Gruende-Liste allein genuegt nicht. Sie kennt nur, was sich
 *    ordentlich angemeldet hat; eine Wiedergabe, die daran vorbeilaeuft,
 *    haelt trotzdem das Mikro — und die Rettung wuerde eine voellig gesunde
 *    Sitzung zerreissen, waehrend Vespera noch spricht.
 */
function etwasKlingt(): boolean {
  try {
    return aktuellerTon()?.klingt === true;
  } catch {
    return false;
  }
}

/**
 * ⚠️⚠️ 24.09.2026 (Tickets 2483/2253) — IM HINTERGRUND WIRD NICHT
 *      FORTGESETZT, SONDERN VORGEMERKT.
 *
 * Nach einer fremden Unterbrechung (`.began`: Anruf, Siri, anderes Audio)
 * gibt iOS einer App im Hintergrund das Mikrofon auch zum FORTSETZEN nicht
 * zurück — `resume()` scheitert mit '!int' (OSStatus 560557684). Alle 18
 * Fälle seit dem 02.08. trugen `vordergrund=nein`, zuletzt 73BDF50D am
 * 23.09. mit `versuch=10`.
 *
 * Jeder `resume()` von hier zählt in der Engine mit: sie versucht selbst
 * zehnmal (`scheduleResumeRetry`), und jeder Anstoß aus der WebView ist ein
 * weiterer Versuch auf DENSELBEN Zähler. Pause-Wache und Nachprüfung
 * zusammen verbrauchten die zehn in rund acht Sekunden; danach schloss die
 * Engine die Sitzung mit `mikro-belegt`.
 *
 * Deshalb stoßen diese beiden im Hintergrund nichts mehr an. Sie merken
 * vor, und beim nächsten Sichtbarwerden wird EINMAL fortgesetzt — dort, wo
 * iOS das Mikrofon hergibt. Mehrere Vormerkungen ergeben einen Versuch.
 *
 * ⚠️ Die Vormerkung gilt nur für DIESE Pause. Verlässt die Engine den
 *    Zustand „pausiert" (sie setzte nach `.ended` selbst fort, oder die
 *    Sitzung ist zu), wird sie verworfen. Sonst setzte das Entsperren eine
 *    SPÄTERE Pause sofort fort — etwa eine, die der Mensch danach über das
 *    Widget selbst gesetzt hat; die gehört der Pause-Wache und ihrer Frist.
 *    Die Vormerkung räumt ihre beiden Zuhörer selbst ab: ein Besitzer.
 */
let vormerkungAbraeumen: (() => void) | null = null;

function imHintergrund(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

function merkeFortsetzenVor(wer: "pause-wache" | "nachpruefung" | "pause-push"): void {
  meldeMikroAnlass(`fortsetzen-vorgemerkt:${wer}`);
  if (vormerkungAbraeumen || typeof document === "undefined") return;
  const abraeumen = (): void => {
    document.removeEventListener("visibilitychange", beiSicht);
    window.removeEventListener("voice-v8:recorder-state", beiZustand);
    vormerkungAbraeumen = null;
  };
  const beiSicht = (): void => {
    if (document.visibilityState !== "visible") return;
    abraeumen();
    void holeFortsetzenNach().catch(() => undefined);
  };
  const beiZustand = (e: Event): void => {
    const st = (e as CustomEvent<{ state?: string }>).detail?.state;
    if (st && st !== "paused") {
      abraeumen();
      meldeMikroAnlass(`fortsetzen-verworfen:${st}`);
    }
  };
  vormerkungAbraeumen = abraeumen;
  document.addEventListener("visibilitychange", beiSicht);
  window.addEventListener("voice-v8:recorder-state", beiZustand);
}

async function holeFortsetzenNach(): Promise<void> {
  await syncFromNative().catch(() => undefined);
  if (getRecorderState() !== "paused") return;
  // 27.10.2026 (Ticket 3369, Prüfer): Hat der Mensch die Tagesaufnahme
  // während der Pause ausgeschaltet, setzt das Öffnen nicht gegen seinen
  // Willen fort — gilt für jede Vormerkung (Pause-Wache, Nachprüfung, Push).
  if (!isDayIntentActive()) return;
  // Wer das Mikro gerade absichtlich hält, setzt selbst fort, wenn er fertig ist.
  // (24.09.2026, Ticket 2202: auch die native Sprechtaste — hatOffenePausenGruende.)
  if (hatOffenePausenGruende() || etwasKlingt()) return;
  // Dieselbe Regel wie die Wache: nur bei BELEGTER Ursache. Eine Pause von
  // Martin oder mit unbekanntem Grund bleibt, bis er selbst fortsetzt.
  if (urteilFuerWache() !== "belegt") {
    meldeMikroAnlass(`fortsetzen-unterlassen:${urteilFuerWache()}`);
    return;
  }
  try {
    localStorage.removeItem(MIC_PAUSE_KEY);
  } catch {
    /* egal */
  }
  meldeMikroAnlass("fortsetzen-nachgeholt:sichtbar");
  await setzeJetztFort();
}

/**
 * Nachprüfen, ob das Fortsetzen ankam. (12.07., Martin: „steht auf
 * Pausiert, verunsichert total" — der Weiter-Befehl konnte STILL scheitern.)
 * Bis zu dreimal wiederholen, danach ehrlich warnen. Läuft im Hintergrund,
 * damit kein Aufrufer 1,5 s hängt.
 *
 * ⚠️ Nur nachfassen, solange KEIN neuer Grund eingetragen ist — sonst
 *    setzt die Prüfung fort, was die Wiedergabe gerade absichtlich anhält.
 */
/** Die eine laufende Nachpruefung — mehrere parallel hiessen mehrere Abrisse. */
let nachpruefungLaeuft: Promise<void> | null = null;
let nachpruefungSeit = 0;
/** Laenger als das laeuft keine ehrliche Nachpruefung — dann haengt sie, und der naechste Griff darf neu. */
const NACHPRUEFUNG_HOECHSTENS_MS = 60_000;

/** Nur Tests. Setzt auch eine offene Vormerkung zurück (24.09.2026). */
export function _nachpruefungZuruecksetzen(): void {
  nachpruefungLaeuft = null;
  nachpruefungSeit = 0;
  vormerkungAbraeumen?.();
}

async function bestaetigeFortsetzung(): Promise<void> {
  // 15.09.2026 (Pruefer-Befund): Jeder Fortsetz-Weg (Knopf, Overlay, Wiedergabe,
  // PTT, Anruf, Wache) startete eine eigene 15-s-Schleife; drei davon rissen
  // im Journal drei Sitzungen hintereinander ab. Es gibt nur eine — und haengt
  // sie (ein Plugin-Aufruf, der nie zurueckkommt), gilt sie nach einer Minute
  // als tot, sonst schluckte sie jeden weiteren Griff fuer immer.
  if (nachpruefungLaeuft && Date.now() - nachpruefungSeit < NACHPRUEFUNG_HOECHSTENS_MS) {
    return nachpruefungLaeuft;
  }
  nachpruefungSeit = Date.now();
  const meine = bestaetigeFortsetzungEinmal().finally(() => {
    if (nachpruefungLaeuft === meine) nachpruefungLaeuft = null;
  });
  nachpruefungLaeuft = meine;
  return meine;
}

async function bestaetigeFortsetzungEinmal(): Promise<void> {
  const plugin = getRecorder();
  if (!plugin) return;
  // Der Stand VOR dem ersten Loesen — danach ist er konstruktionsbedingt leer.
  let haltVorher = "";
  try {
    const w = wiedergabeHaltStand();
    haltVorher = `spielend=${w.spielend} hifi=${w.hiFi ? 1 : 0} vorwaermung=${w.vorwaermung ? 1 : 0} stimme=${w.stimme ? 1 : 0} saldo=${w.saldo}`;
  } catch {
    /* Diagnose ist Kuer */
  }
  /*
   * ⚠️ 08.09.2026 — der Stand ZU BEGINN. Aendert er sich, war jemand am
   *    Mikrofon (PTT, Diktat, Wiedergabe), und die Rettung tritt zurueck —
   *    auch dann, wenn er sich zwischen zwei Blicken wieder abgemeldet hat.
   *    Die Gruende-Menge allein sieht das nicht (siehe micAnmeldungen).
   */
  const standZuBeginn = micAnmeldungen;
  /*
   * 15.09.2026: Auch die Wiedergabe-Seite zaehlt (Hi-Fi an, Element spielt,
   * Vorwaermung laeuft). Bisher sah die Rettung nur die Gruende-Menge und
   * den Leitstand — und riss waehrend einer laufenden Hoer-Sitzung die
   * Tagesaufnahme ab, weil zwischen zwei Abschnitten kurz „nichts klang".
   */
  const wiedergabeHaelt = (): boolean => {
    try {
      const w = wiedergabeHaltStand();
      return w.spielend > 0 || w.hiFi || w.vorwaermung;
    } catch {
      return false;
    }
  };
  // 24.09.2026 (Ticket 2202): und die native Sprechtaste — genau sie war am
  // 08.09. die Hand, die die Rettung nicht sah (siehe pttNativSeit).
  const fremdeHand = (): boolean =>
    // Martin pausiert mitten in der Nachpruefung: dann ist das SEINE Hand.
    istNutzerPause() ||
    micAnmeldungen !== standZuBeginn ||
    micPauseGruende.size > 0 ||
    etwasKlingt() ||
    wiedergabeHaelt() ||
    pttNativHaelt();
  for (let versuch = 1; versuch <= FORTSETZ_VERSUCHE; versuch++) {
    await new Promise((r) => setTimeout(r, FORTSETZ_TAKT_MS));
    if (fremdeHand()) return;
    await syncFromNative().catch(() => undefined);
    if (fremdeHand()) return;
    if (getRecorderState() !== "paused") return;
    // Ticket 2728: ein Anruf hält das Mikro — die Engine setzt danach selbst fort.
    if (await anrufHaeltMikro()) {
      meldeMikroAnlass("fortsetzen-aufgeschoben:anruf");
      return;
    }
    // 24.09.2026 (Ticket 2483): im Hintergrund kein weiterer Anstoß — er
    // verbraucht nur die zehn Versuche der Engine. Siehe merkeFortsetzenVor.
    if (imHintergrund()) {
      merkeFortsetzenVor("nachpruefung");
      return;
    }
    // Bei jedem Anlauf neu freiraeumen: eine Wiedergabe kann sich in der
    // Zwischenzeit erneut angemeldet und die Session wieder gepinnt haben.
    loeseWiedergabeHalt(`fortsetzen-versuch-${versuch}`);
    /*
     * Die Kategorie zurueckschalten, bevor fortgesetzt wird (pre.3140).
     *
     * ⚠️⚠️ 22.09.2026 — DIESE ZEILE HILFT NICHT. SIE BLEIBT TROTZDEM.
     *
     * Eingebaut am 21.09. abends auf eine Ursache hin, die ich am naechsten
     * Morgen selbst widerlegt habe: „sieben gescheiterte Fortsetzungen, jede
     * sekundengenau auf einem `playback-end`, also steht die iOS-Sitzung auf
     * `.playback` und gibt kein Mikro."
     *
     * Beides war falsch. Die `playback-end`-Zeilen ERZEUGT der Fortsetz-
     * Versuch selbst (`loeseWiedergabeHalt` meldete sie eine Zeile weiter
     * oben) — Wirkung, nicht Ursache. Und in ELF von ELF Faellen der Nacht
     * stand die Kategorie gemessen auf `PlayAndRecord` bei
     * `playbackHiFiActive=false`, nie auf `.playback`.
     *
     * Sie bleibt, weil sie den Nutzer-Weg aus `raeumeTonFuerAufnahme`
     * spiegelt und nichts kostet, wenn schon zurueckgeschaltet ist. Sie ist
     * aber KEINE Erklaerung — wer hier nach der Ursache sucht, liest die
     * Meldung des Versuchs darunter, nicht diesen Aufruf.
     *
     * ⚠️ Gefahrlos an dieser Stelle: `fremdeHand()` ist unmittelbar davor
     *    zweimal geprueft worden. Klingt wirklich noch etwas, sind wir hier
     *    laengst ausgestiegen.
     */
    await setPlaybackHiFi(false).catch(() => null);
    /*
     * ⚠️⚠️ 22.09.2026 (Ticket 2253) — WAS `resume()` SAGT, STAND NIRGENDWO.
     *
     * Elf gescheiterte Fortsetzungen in der Nacht auf den 22.09., und in
     * ALLEN ELF war die iOS-Kategorie schon `PlayAndRecord` und
     * `playbackHiFiActive=false` — also genau der Zustand, in dem ein
     * Mikrofon zu haben ist. Die Kategorie war nie das Hindernis.
     *
     * Was fehlt, ist die Antwort der Engine selbst. `resume()` wurde hier
     * blind erwartet: wirft es, verlaesst der Fehler die ganze Funktion und
     * die Rettung unten laeuft nie; kehrt es still zurueck, sieht niemand,
     * dass nichts geschah. Beides sieht im Protokoll gleich aus.
     *
     * Ab jetzt sagt jeder Versuch, was er bewirkt hat.
     */
    let antwort = "still";
    // Letzter Blick: die Tonraeumung oben hat gewartet, Martin kann inzwischen
    // pausiert haben.
    if (fremdeHand()) return;
    try {
      await plugin.resume();
    } catch (err) {
      antwort = `fehler:${err instanceof Error ? err.message : String(err)}`;
    }
    meldeMikroAnlass(
      `fortsetzen-versuch-${versuch}:${antwort}` +
        `-sicht=${typeof document !== "undefined" ? document.visibilityState : "?"}`,
    );
  }
  await syncFromNative().catch(() => undefined);
  if (getRecorderState() !== "paused") return;
  // ⚠️ Der letzte Blick vor dem Abriss — hier haengt Martins Mikrofon dran.
  if (fremdeHand()) return;
  if (await anrufHaeltMikro()) {
    meldeMikroAnlass("fortsetzen-aufgeschoben:anruf");
    return;
  }
  /*
   * ⚠️ 07.09.2026 — der letzte Ausweg RETTET jetzt, statt zu bitten.
   *
   * Hier stand ein Hinweis: *„bitte oben auf Pausiert tippen und auf Weiter
   * druecken."* Martin: *„Auch hier laesst sich der Pause-Knopf nicht loesen
   * ... ich muss komplett neu starten und dann wieder auf Aufnahme
   * druecken."* Der Rat schickte ihn also genau zu dem Knopf, der aus
   * demselben Grund nicht ging wie dieser Versuch — dieselbe Engine,
   * dasselbe fehlende Mikrofon.
   *
   * Eine Sitzung, die nach dreieinhalb Sekunden und drei Anlaeufen nicht
   * zurueckkommt, ist tot. Das Ziel ist nicht DIESE Sitzung, sondern dass
   * der Alltag weiter aufgezeichnet wird. Also: sauber abschliessen (das
   * Aufgenommene ist damit gesichert) und neu beginnen.
   */
  /*
   * ⚠️⚠️ 17.09.2026 — IM HINTERGRUND WIRD NICHT ABGERISSEN.
   *
   * Martin: *„Dieses verdammte Aufnahmesystem geht immer auf Pause … ich
   * mache einen Vortrag und jedes Mal muss ich schauen, dass es gar nicht
   * mehr aufnimmt."* Und dazu sein Verdacht: *„Vielleicht hat das auch damit
   * zu tun, wenn ich den Display ausschalte."*
   *
   * Er hat recht, und der Grund steht seit dem 24.08. in dieser Codebasis:
   *
   *   iOS gibt einer App das Mikrofon nur, wenn sie im VORDERGRUND ist.
   *   Eine bereits laufende Audio-Session darf im Hintergrund weiterlaufen,
   *   eine NEUE aber nicht beginnen.
   *
   * Die Rettung hier tut genau das Verbotene: sie schliesst die Sitzung ab
   * und beginnt eine neue. Bei ausgeschaltetem Display merkt `beginSession`
   * den Start nur VOR („vorgemerkt bis zum Vordergrund") — aufgenommen wird
   * bis zum naechsten Blick aufs Telefon nichts.
   *
   * ⚠️⚠️ 24.09.2026 (Tickets 2483/2253) — RICHTIGGESTELLT. Hier stand:
   *      „`resume()` darf im Hintergrund, `beginSession` nicht." Das ist
   *      widerlegt. Nach einer fremden Unterbrechung (`.began`) verweigert
   *      iOS auch das Fortsetzen im Hintergrund: '!int' (OSStatus
   *      560557684), in allen 18 Fällen seit dem 02.08. mit
   *      `vordergrund=nein`. Beide Wege brauchen dann den Vordergrund.
   *      Belegt ist nur der fremde Fall — ob ein Fortsetzen nach einer
   *      EIGENEN Pause (Vorlesen, Sprechtaste) im Hintergrund gelingt, ist
   *      damit nicht entschieden.
   *
   * Der Riegel bleibt trotzdem richtig: eine pausierte Sitzung kommt beim
   * nächsten Blick aufs Telefon als DIESELBE zurück; eine abgerissene
   * hinterlässt zwei Einträge und eine Lücke dazwischen.
   *
   * Belegt am 17.09.: fuenf Sitzungen in zweieinhalb Stunden statt einer,
   * mit Luecken von 25 s bis 2,5 min.
   */
  if (imHintergrund()) {
    meldeMikroAnlass("fortsetzen-aufgeschoben:hintergrund");
    /*
     * ⚠️ Die Marke MUSS zurueck. Ganz oben in dieser Funktion wird sie
     *    entfernt — ein spaeterer Anlauf (die Wache beim Zurueckkommen)
     *    faende danach „weder Grund noch Marker" und stiege sofort wieder
     *    aus. Aufschieben ohne diese Zeile hiesse: fuer immer pausiert.
     *
     *    Genau diese Falle haette mein erster Riegel gestellt.
     */
    try {
      localStorage.setItem(MIC_PAUSE_KEY, String(Date.now()));
    } catch {
      /* Quota/Privat-Modus — die Wache heilt trotzdem ueber den Zustand */
    }
    console.warn(
      "[recorder] Display aus — die Sitzung bleibt pausiert statt abgerissen " +
        "(iOS gibt im Hintergrund kein Mikro für eine NEUE Aufnahme).",
    );
    return;
  }

  meldeMikroAnlass("fortsetzen-gescheitert:neue-sitzung");
  /*
   * ⚠️ 14.09.2026 — WARUM kam sie nicht zurueck?
   *
   * Fuenf solche Rettungen an einem Tag. Drei lagen 14-31 Sekunden nach
   * einem Ton- oder PTT-Ereignis — da lief plausibel noch etwas. Einer
   * (17:49) hatte ZEHN MINUTEN keinerlei Ton-Aktivitaet davor und
   * scheiterte trotzdem. Welcher Halt dort noch stand, stand NIRGENDS.
   *
   * Nativ entscheidet `Tonbelegung.aktiv` (Telefon, Vespera-Anruf,
   * Wiedergabe) — und `resume()` kehrt dann STUMM zurueck, ohne Fehler und
   * ohne Protokoll. Von hier aus sieht man wenigstens die Wiedergabe-Seite.
   * Damit trennt der naechste Vorfall "es lief wirklich noch etwas" von
   * "ein Halt ist haengen geblieben".
   *
   * ⚠️ Bewusst WENIGE unterscheidbare Werte: `art` wird gruppiert
   *    ausgewertet, beliebiger Text darin waere unbrauchbar.
   */
  try {
    const w = wiedergabeHaltStand();
    meldeMikroAnlass(
      w.spielend > 0
        ? "fortsetzen-halt:wiedergabe-laeuft"
        : /*
           * ⚠️ 19.09.2026 — DIE STIMME KAM HIER NIE VOR, und sie ist die
           *    haeufigste Tonquelle des Systems. Am 19.09. um 15:56:37
           *    scheiterte das Fortsetzen, diese Zeile meldete
           *    "nichts-offen", und vier Sekunden davor stand
           *    `vorlesen-stueck` im selben Protokoll. Die Diagnose vom
           *    14.09. sollte "es lief wirklich etwas" von "ein Halt haengt"
           *    trennen — mit dieser Luecke konnte sie es nie.
           */
          w.stimme
          ? "fortsetzen-halt:stimme-laeuft"
          : w.hiFi
            ? "fortsetzen-halt:hifi-haengt"
            : w.vorwaermung
              ? "fortsetzen-halt:vorwaermung"
              : "fortsetzen-halt:nichts-offen",
    );
    // Was VOR dem ersten Loesen stand — die Zeile, die bisher fehlte.
    console.warn(`[recorder] Halt vor der Rettung: ${haltVorher || "unbekannt"}`);
    meldeMikroAnlass(`fortsetzen-halt-vorher:${haltVorher.replace(/[^a-z0-9=]/g, "-") || "unbekannt"}`);
  } catch {
    /* Diagnose darf die Rettung nie aufhalten */
  }
  try {
    /*
     * ⚠️ `stopRecording()`, NICHT `stopRecordingForUser()`. Letzteres setzt
     *    das bewusste Aus und schaltet „Durchgehend" ab — das waere hier
     *    genau falsch: Der Nutzer hat nichts abgeschaltet, wir raeumen nur
     *    eine tote Sitzung weg.
     */
    const lief = leseZuletztLief();
    await stopRecording("rettung-nach-gescheitertem-fortsetzen");
    await syncFromNative().catch(() => undefined);
    leereGruende("rettung");
    try {
      localStorage.removeItem(MIC_PAUSE_KEY);
    } catch {
      /* egal */
    }
    await startRecording();
    /*
     * ⚠️⚠️ 08.09.2026 — DIE TAGES-ABSICHT ZURUECKHOLEN. Das ist die Wurzel
     *      von „es faellt immer wieder aus".
     *
     * Martin: *„Egal wie oft ich hinschaue, es ist immer wieder passiert
     * oder ausgefallen. Echt nervig."*
     *
     * In der nativen Engine steht in `stop(reason:)`:
     *
     *     // Bewusster Stopp beendet die Tages-Absicht:
     *     // kein Auto-Resume beim naechsten App-Start.
     *     clearDayIntent()
     *
     * JEDER Stopp loescht sie also — auch unserer hier, der gar kein
     * bewusster ist. Danach lief zwar eine neue Sitzung, aber das Telefon
     * wusste nicht mehr, dass es den ganzen Tag aufnehmen soll: Beim
     * naechsten App-Start, bei der naechsten Unterbrechung durch iOS, beim
     * naechsten Ende blieb es einfach aus.
     *
     * ⚠️ Alle anderen Aufrufer setzen sie danach neu (aufnahme-wache.ts,
     *    alltag-view.ts). Nur diese Rettung nicht — sie war die einzige
     *    Stelle, die eine Sitzung beendet und den Auftrag dabei verliert.
     */
    if (dauerGewuenscht()) await setAlwaysDayRecording(true);
    merkeNeubeginn(lief);
    /*
     * ⚠️ 08.09.2026 — NACHSEHEN, nicht glauben.
     *
     * Am 08.09. um 12:10:20 lief genau dieser Weg: Die Rettung schloss die
     * Sitzung, meldete „neue Sitzung begonnen" — und danach kam fuenf
     * Minuten lang kein einziges Haeppchen mehr an. Der Neubeginn war still
     * gescheitert, und die Meldung behauptete das Gegenteil.
     *
     * Kommt die Aufnahme nicht zurueck, sagen wir das laut. Die Wache
     * (aufnahme-wache.ts) sieht dann im naechsten Takt nach und beginnt
     * erneut — sie kann das nur, wenn hier niemand Erfolg vortaeuscht.
     */
    await new Promise((r) => setTimeout(r, 1200));
    await syncFromNative().catch(() => undefined);
    if (getRecorderState() === "recording") {
      console.warn("[recorder] Pause liess sich nicht loesen — neue Sitzung begonnen");
    } else {
      meldeMikroAnlass(`neubeginn-ohne-aufnahme:${getRecorderState()}`);
      console.warn(
        `[recorder] Neubeginn brachte keine Aufnahme (Zustand: ${getRecorderState()}) — die Wache übernimmt`,
      );
    }
    return;
  } catch (err) {
    console.warn("[recorder] auch der Neubeginn scheiterte:", err);
  }
  try {
    window.dispatchEvent(
      new CustomEvent("voice-v8:toast", {
        detail: {
          message:
            "Die Tages-Aufnahme liess sich nicht fortsetzen. Ich habe es mit einer neuen Sitzung versucht — bitte kurz nachsehen.",
          kind: "info",
        },
      }),
    );
  } catch {
    /* kein window (Tests) */
  }
}

/**
 * Die Knöpfe. NUR für eine Nutzerhandlung — programmatische Pfade gehen
 * über pauseDayRecorderForMic / resumeDayRecorderAfterMic (ein Besitzer).
 *
 * ⚠️ 27.09.2026 (Ticket 2708, Nachbesserung) — ZWEI Befehle, kein Umschalter.
 *
 * Hier stand `toggleRecorderPause`: Er las den ECHTEN Zustand und kippte
 * ihn. Der Knopf zeigt aber, was Martin will. Stand noch „Fortsetzen" da,
 * obwohl die Aufnahme schon wieder lief (zweiter Tipp, bevor die Anzeige
 * nachkam; ein Fortsetzen übers Widget), pausierte ein Tipp auf
 * „Fortsetzen" die Aufnahme — und im Journal stand es als gewollte
 * Knopf-Pause. Dieselbe Bauart hatte schon am 05.09. app.ts die laufende
 * Aufnahme in Pause gekippt. Jetzt tut jeder Befehl nur das, was auf dem
 * Knopf stand, und sonst nichts.
 */
export async function pauseRecorderForUser(): Promise<void> {
  const plugin = getRecorder();
  if (!plugin) return;
  await syncFromNative().catch(() => undefined);
  const echt = getRecorderState();
  if (echt !== "recording") {
    console.log(`[recorder] Nichts zu pausieren — Zustand: ${echt}`);
    return;
  }
  // Ticket 2708: VOR dem Befehl merken — der Zustand springt, während
  // pause() noch läuft, und die Meldung muss den Knopf schon kennen.
  knopfPauseUm = Date.now();
  merkeBesitzer("nutzer", "knopf");
  try {
    await plugin.pause();
  } catch (e) {
    vergissBesitzer();
    throw e;
  }
}

/**
 * ⚠️ 07.09.2026 — das Fortsetzen geht denselben Weg wie alle anderen.
 *
 * Martin, über das Aufnahme-Blatt aus dem Widget: *„Beim Widget läuft der
 * Recorder. Wenn ich auf Pause drücke, geht er in Pause, aber wenn ich dann
 * auf den Play-Button wieder drücke, dann lässt er sich die Pause nicht mehr
 * wegnehmen, damit er wieder aufnimmt."*
 *
 * Hier stand ein blankes `plugin.resume()`. Ihm fehlte BEIDES, was
 * `setzeJetztFort()` seit heute früh tut: die Tonanlage freiräumen (in der
 * Kategorie `.playback` gibt iOS kein Mikrofon her — der resume verpufft
 * lautlos) und nachprüfen, ob das Fortsetzen überhaupt ankam (drei Anläufe,
 * dann eine frische Sitzung). Die Rettungskette hing also an jedem
 * programmatischen Pfad — nur nicht an dem Knopf, den Martin danach drückt.
 */
export async function resumeRecorderForUser(): Promise<void> {
  const plugin = getRecorder();
  if (!plugin) return;
  await syncFromNative().catch(() => undefined);
  const echt = getRecorderState();
  if (echt !== "paused") {
    console.log(`[recorder] Nichts fortzusetzen — Zustand: ${echt}`);
    return;
  }
  // Eine ausdrückliche Handlung hebt fremde Gründe auf — sonst hält ein
  // vergessener Eintrag („wiedergabe", nie abgemeldet) die Aufnahme
  // für immer an, und der Knopf wirkt kaputt.
  vergissBesitzer();
  leereGruende("nutzerwunsch");
  // Ticket 2708: auch ohne offenen Grund ist dieses Fortsetzen unseres.
  // leereGruende merkt sich nur etwas, wenn die Menge voll war — nach
  // einer Pause per Knopf ist sie leer, und das Fortsetzen stand als
  // „weiter-fremd" im Journal.
  merkeAustrag("nutzerwunsch");
  try {
    localStorage.removeItem(MIC_PAUSE_KEY);
  } catch {
    /* egal */
  }
  await setzeJetztFort();
}

// 2026-07-23 (Stabilisierung Teil 2): Diktat-Mikro-Koordination — die
// Tagesaufnahme wird fuers Roh-Mikro des Diktats pausiert statt zu
// kollidieren. Nur EIGENE Pausen werden fortgesetzt; 5-min-Sicherheitsnetz.
/**
 * WER die Tagesaufnahme gerade pausiert hat. (05.09.2026)
 *
 * Martin: *„Die Aufnahme ist gelaufen, aber es steht jetzt auf pausiert,
 * nachdem ich eine Anfrage gestellt habe — das müsste ja normal
 * weiterlaufen, oder?"*
 *
 * Hier stand ein einzelnes `micPauseActive`. FÜNF Stellen teilen sich diese
 * Sperre: PTT (zweimal), die Tiefen Fragen, das Live-Diktat, die
 * Selbstheilung — und seit heute die Ton-Schonung, die während einer
 * Wiedergabe pausiert.
 *
 * Mit EINEM Schalter geht das so schief: Die Wiedergabe pausiert die
 * Aufnahme, Martin drückt PTT, PTT setzt am Ende fort und löscht den
 * Schalter. Endet danach die Wiedergabe, findet ihr `resume` einen
 * gelöschten Schalter vor und tut NICHTS — die Aufnahme bleibt pausiert.
 * Genau das ist ihm passiert.
 *
 * Ein Schalter kann nicht zählen, wie viele Gründe es gerade gibt. Eine
 * Menge kann es: Wer pausiert, trägt sich ein; wer fortsetzt, trägt sich
 * aus. Fortgesetzt wird erst, wenn NIEMAND mehr einen Grund hat.
 *
 * ⚠️ Der Marker im Speicher bleibt: Nach einem Reload weiß nur er noch,
 *    dass überhaupt pausiert wurde — die Menge ist dann leer, die Aufnahme
 *    aber immer noch angehalten.
 */
const micPauseGruende = new Set<string>();

/**
 * Die Gruende leeren — und sagen, WER das war. (21.09.2026, Ticket 2434)
 *
 * ⚠️ Der Anlass: Im Journal standen „tagesaufnahme-weiter-fremd" ohne
 *    erkennbaren Halt davor, und ich habe daraus zuerst geschlossen, die
 *    Weboberflaeche sei nicht gelaufen. Das war falsch — die Menge wird an
 *    FUENF Stellen geleert, und danach sieht jede eigene Pause aus wie eine
 *    fremde.
 *
 *    „fremd" heisst heute nur: kein Grund EINGETRAGEN. Ob nie einer da war
 *    oder ob ihn jemand weggeraeumt hat, war nicht zu unterscheiden. Diese
 *    Zeile macht den Unterschied sichtbar.
 *
 * ⚠️ Nur melden, wenn wirklich etwas drinstand. Ein Leeren der leeren Menge
 *    ist kein Ereignis und waere nur Rauschen.
 */
function leereGruende(wer: string): void {
  if (micPauseGruende.size === 0) return;
  const waren = [...micPauseGruende].join(",");
  micPauseGruende.clear();
  meldeMikroAnlass("pausengruende-geleert", `von=${wer} waren=${waren}`);
  merkeAustrag(wer);
}

/**
 * Wer sich zuletzt aus der Menge ausgetragen hat, und wann.
 * (23.09.2026, Ticket 2434)
 *
 * ⚠️ Das Austragen geschieht VOR dem Fortsetzen — beim Umspringen auf
 *    „recording" ist die Menge darum schon leer, und unser eigenes
 *    Fortsetzen sah aus wie ein fremdes (siehe fremdePauseMeldung).
 *    Gemerkt wird an beiden Stellen, die austragen: beim einzelnen Grund
 *    in resumeDayRecorderAfterMic und beim Leeren in leereGruende.
 */
let zuletztAusgetragen: { grund: string; um: number } | null = null;

function merkeAustrag(grund: string): void {
  zuletztAusgetragen = { grund, um: Date.now() };
}

/**
 * Nur für Tests. (23.09.2026, Ticket 2434)
 *
 * ⚠️ Ohne das wanderte der Austrag mit gestellter Uhr in den nächsten
 *    Test: Die Notbremse nach fünf Minuten lag dort in der Zukunft, und
 *    ein echtes fremdes Fortsetzen galt als eigenes.
 *    Seit Ticket 2708 gilt dasselbe für den gemerkten Pause-Knopf.
 */
export function _austragVergessen(): void {
  zuletztAusgetragen = null;
  knopfPauseUm = 0;
}
/**
 * Wie oft sich seither jemand fuers Mikrofon angemeldet hat. (08.09.2026)
 *
 * ⚠️ Martin: *„Jetzt nimmt er wieder nicht auf bei Push to Talk. Ich seh
 *    weder wie der Text entsteht noch, dass aufgenommen wird."*
 *
 *    Gemessen in `geraet_ereignis`:
 *
 *      16:07:27  ptt-start nativ (rec=90 ms)       ← PTT laeuft
 *      16:07:30  ptt-warmmikro-abbruch:ptt-aufnahme
 *      16:07:32  fortsetzen-gescheitert:neue-sitzung  ← die Rettung feuert
 *
 *    Die Rettung riss die Tages-Sitzung ab, WAEHREND er ins Mikrofon
 *    sprach — und nahm ihm damit das Mikrofon weg. Ihre Wache prueft die
 *    Gruende-Menge zwar bei jedem Durchgang, aber zwischen den Pruefungen
 *    liegen `await`s (resume, syncFromNative). Meldet sich PTT genau darin
 *    an und wieder ab, ist die Menge am naechsten Blick wieder leer: Die
 *    Rettung sieht nichts und schlaegt zu.
 *
 *    ⚠️ Heute frueh habe ich das Fenster von 4,5 auf 15 Sekunden
 *       vergroessert — richtig gegen das Abreissen, aber es verdreifacht
 *       genau diese Ueberschneidung. Deshalb zaehlt jetzt jede Anmeldung:
 *       Aendert sich die Zahl waehrend der Rettung, war jemand am Mikrofon,
 *       und die Rettung tritt zurueck.
 */
let micAnmeldungen = 0;
let micPauseTimer: number | null = null;
/** Persistenter Marker: Mikro-Pause ueberlebt App-Reloads (2026-07-23:
 *  ein Reload toetete das In-Memory-Flag und die Aufnahme blieb fuer
 *  immer pausiert — Session wurde vom Server-Reaper als verwaist
 *  finalisiert, Martin: "ich habe nie auf Pause gedrueckt"). */
const MIC_PAUSE_KEY = "voice-v8:mic-pause-at";

/**
 * Haelt gerade jemand die Aufnahme absichtlich an? (06.09.2026)
 *
 * Die Wache in `aufnahme-wache.ts` muss das wissen, bevor sie fortsetzt —
 * sonst funkt sie einem laufenden Diktat dazwischen und nimmt Stille auf.
 */
export function hatOffenePausenGruende(): boolean {
  // 24.09.2026 (Ticket 2202): die native Sprechtaste zählt mit — sie hält
  // das Mikro schon, bevor ihr Grund „ptt" eingetragen ist (siehe
  // pttNativSeit).
  return micPauseGruende.size > 0 || pttNativHaelt();
}

/** Nur fuer Tests und Protokolle: welche Gruende gerade eingetragen sind. */
export function offenePausenGruende(): string[] {
  return [...micPauseGruende];
}

/**
 * Einen Pausengrund eintragen, ohne dass etwas aufnimmt. (Ticket 2849)
 *
 * Für den Zuruf „Alltag starten“ MITTEN im Anruf: `pauseDayRecorderForMic` trägt nur ein, wenn der
 * Rekorder läuft. Steht er auf „idle“, fehlte der Grund — und die Wache (aufnahme-wache.ts) finge neben
 * dem Anruf an (Audio-Engine-Fehler 4010). Mit dem Grund wartet sie; das Auflegen meldet ihn über
 * `resumeDayRecorderAfterMic` wieder ab. Dieselbe Notbremse (5 Minuten) wie bei den anderen Gründen.
 */
export function haltePausenGrund(grund: string): void {
  micPauseGruende.add(grund);
  micAnmeldungen++;
  if (micPauseTimer === null) {
    micPauseTimer = window.setTimeout(() => {
      leereGruende("notbremse");
      void resumeDayRecorderAfterMic();
    }, 5 * 60_000);
  }
}

export async function pauseDayRecorderForMic(
  grund = "mikro",
): Promise<boolean> {
  try {
    // 2026-07-25 (Martin, "PTT nimmt nichts auf, waehrend die Tagesaufnahme
    // laeuft"): NICHT mehr dem zwischengespeicherten Zustand glauben. Der wird
    // nur alle 15 s abgeglichen; in der Luecke (nach Auto-Neustart, nach einer
    // Unterbrechung) stand hier etwas anderes als "recording" — die Pause
    // unterblieb, PTT griff ins belegte Mikro und nahm STILLE auf.
    await syncFromNative().catch(() => undefined);
    const echt = getRecorderState();
    if (echt !== "recording") {
      // Kennzeichen aufraeumen, falls es von einem frueheren Versuch haengt.
      /*
       * ⚠️ Ist sie PAUSIERT und es gibt schon einen Grund, tragen wir uns
       *    trotzdem ein: Sonst setzt das Ende dieses zweiten Grundes fort,
       *    während der erste noch gilt (Martins Fall vom 05.09.).
       */
      if (echt === "paused" && micPauseGruende.size > 0) {
        micPauseGruende.add(grund);
        micAnmeldungen++;
        return true;
      }
      if (echt === "idle") leereGruende("engine-idle");
      return false;
    }
    // Die Engine nimmt nachweislich auf — ein "schon pausiert" waere also
    // veraltet und wuerde die Uebernahme faelschlich verhindern.
    leereGruende("engine-nimmt-auf");
    micPauseGruende.add(grund);
    micAnmeldungen++;
    merkeBesitzerSchwach("system", grund);
    try {
      localStorage.setItem(MIC_PAUSE_KEY, String(Date.now()));
    } catch {
      /* Quota/Privat-Modus — Wache heilt trotzdem */
    }
    const angehalten = await halteJetztAn();
    if (!angehalten) {
      /*
       * ⚠️ Ehrlich scheitern statt Erfolg melden. Wer `true` bekommt,
       *    greift gleich nach dem Mikrofon — und traefe im Browser auf
       *    eine Aufnahme, die weiterlaeuft.
       */
      micPauseGruende.delete(grund);
      if (micPauseGruende.size === 0) vergissBesitzer();
      return false;
    }
    micPauseTimer = window.setTimeout(
      () => {
        /*
         * ⚠️ Die Notbremse räumt ALLE Gründe. Nach fünf Minuten stimmt
         *    keiner mehr — wer sie einträgt und nie abmeldet, ist ein
         *    Fehler, und eine Aufnahme, die deswegen für immer steht, ist
         *    der teurere.
         */
        leereGruende("notbremse");
        void resumeDayRecorderAfterMic();
      },
      5 * 60_000,
    );
    console.log(`[recorder] Tagesaufnahme pausiert (${grund})`);
    return true;
  } catch {
    micPauseGruende.delete(grund);
    return false;
  }
}

export async function resumeDayRecorderAfterMic(
  grund = "mikro",
): Promise<void> {
  // 2026-07-23: Auch OHNE In-Memory-Flag heilen — nach einem Reload weiss
  // nur noch der persistierte Marker von der Mikro-Pause.
  let persisted = false;
  try {
    persisted = localStorage.getItem(MIC_PAUSE_KEY) !== null;
  } catch {
    /* egal */
  }
  /*
   * Diesen Grund abmelden — fortgesetzt wird erst, wenn KEINER mehr gilt.
   * Ohne das setzte der erste Fertige fort, während ein anderer noch das
   * Mikro brauchte (05.09.).
   */
  if (micPauseGruende.delete(grund)) merkeAustrag(grund);
  if (micPauseGruende.size > 0) {
    console.log(
      `[recorder] noch pausiert — offene Gruende: ${[...micPauseGruende].join(", ")}`,
    );
    return;
  }
  if (!persisted && micPauseGruende.size === 0 && !micPauseTimer) {
    // Weder ein laufender Grund noch ein Marker: hier war nichts zu tun.
    return;
  }
  try {
    localStorage.removeItem(MIC_PAUSE_KEY);
  } catch {
    /* egal */
  }
  if (micPauseTimer) {
    window.clearTimeout(micPauseTimer);
    micPauseTimer = null;
  }
  try {
    // 2026-07-25 (Martin: "Aufnahme geht wieder nicht, musste neu starten"):
    // DERSELBE Fehler wie beim Pausieren, nur in der Gegenrichtung — hier
    // entschied der zwischengespeicherte Zustand, der nur alle 15 s
    // abgeglichen wird. Stand dort nicht exakt "paused", unterblieb das
    // Fortsetzen und die Tagesaufnahme blieb nach jedem Push-to-Talk liegen
    // (belegt: 61 Haeppchen, letzte Aufzeichnung exakt zum PTT-Zeitpunkt).
    // Erst den ECHTEN Zustand holen, dann entscheiden.
    await syncFromNative().catch(() => undefined);
    if (getRecorderState() === "paused") {
      // 2026-07-25 (Martin, WURZEL von "mikro-belegt"): Das Warm-Mikro, das
      // Push-to-Talk fuer den schnellen naechsten Start haelt, BLOCKIERT die
      // Rueckkehr der Tagesaufnahme — iOS gibt das Mikro nicht zweimal her.
      // Die native Engine bekam es nicht und beendete die Session mit
      // endReason=mikro-belegt (im Server-Log mehrfach belegt).
      // Der START macht das laengst richtig (Ereignis + 450 ms warten,
      // siehe startRecording); beim FORTSETZEN fehlte es. Exakt derselbe
      // Mechanismus, damit es nur EINE Wahrheit gibt.
      try {
        const detail: { held?: boolean } = {};
        window.dispatchEvent(
          new CustomEvent("voice-v8:recorder-will-start", { detail }),
        );
        if (detail.held) await new Promise((r) => setTimeout(r, 450));
      } catch {
        /* kein window (Tests) */
      }
      await setzeJetztFort();
      console.log("[recorder] Tagesaufnahme nach Diktat fortgesetzt");
    } else {
      console.log(
        "[recorder] kein Fortsetzen noetig — Zustand:",
        getRecorderState(),
      );
    }
  } catch {
    /* Watchdogs/Selfheal fangen den Rest */
  }
}

/** Gepufferte Häppchen jetzt hochladen versuchen (z.B. beim Öffnen der Ansicht). */
export async function flushRecorder(): Promise<void> {
  await getRecorder()?.flush();
}

/**
 * Kompakte Alltags-Info ans Home-Screen-Widget durchreichen (Audible-Karte).
 * Fire-and-forget; alte App-Versionen ohne widgetUpdate-Methode → stiller No-op
 * (kein Crash, kein Fehler an den Aufrufer).
 */
// 2026-07-09 (Martin, Widget-Palette-Sync): aktuelle Palette-Farben aus dem
// gerenderten DOM lesen (aufgelöster Hell/Dunkel-Modus) und als "r,g,b" ans
// Widget durchreichen. Eine unsichtbare Sonde lässt den Browser var(--x) zu
// rgb() auflösen — robust für hex/rgb/hsl. Hairline bleibt bewusst weg (hat
// niedrige Alpha; das Widget nutzt dafür seinen dezenten adaptiven Fallback).
function readWidgetColors(): {
  uiBg?: string;
  uiAccent?: string;
  uiStar?: string;
  uiInk?: string;
  uiInkSoft?: string;
  uiIsDark?: boolean;
  uiPalette?: string;
} {
  if (typeof document === "undefined" || !document.body) return {};
  const probe = document.createElement("span");
  probe.className = "css-var-probe";
  document.body.appendChild(probe);
  const rgb = (cssVar: string): string | undefined => {
    probe.style.color = "";
    probe.style.color = `var(${cssVar})`;
    const c = getComputedStyle(probe).color; // "rgb(r, g, b)" / "rgba(...)"
    const m = c.match(/(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
    return m ? `${m[1]},${m[2]},${m[3]}` : undefined;
  };
  try {
    const uiBg = rgb("--paper");
    let uiIsDark = false;
    if (uiBg) {
      const [r, g, b] = uiBg.split(",").map(Number);
      uiIsDark = 0.299 * r + 0.587 * g + 0.114 * b < 140;
    } else if (document.documentElement.dataset.mode === "dark") {
      uiIsDark = true;
    }
    const pal = document.documentElement.dataset.palette || "salbei";
    return {
      uiBg,
      uiAccent: rgb("--ember"),
      uiStar: rgb("--ember-soft"),
      uiInk: rgb("--ink"),
      uiInkSoft: rgb("--ink-soft"),
      uiIsDark,
      uiPalette: pal,
    };
  } finally {
    probe.remove();
  }
}

// 2026-07-15 (Martin, "Musik/Transkription blechern ueber Bluetooth"):
// reine Wiedergabe in A2DP-Qualitaet statt HFP-Telefonprofil anfordern.
// active=true beim Player-Start, false bei Pause/Ende. Web-No-op ohne
// native Huelle (nur nativ hat eine AVAudioSession).
// 2026-07-15 (Martin, On-Device-Diagnose): der echte AVAudioSession-Zustand.
interface AudioSessionState {
  category?: string;
  mode?: string;
  options?: string[];
  outputs?: string[];
  playbackHiFiActive?: boolean;
  callActive?: boolean;
  /** Nur auf Bitte, ab Build 87 — siehe rekorderMessungZeile. */
  rekorderMessung?: Record<string, unknown>;
  /** Ticket 2643 (ab Build 93): darf die App das Mikrofon benutzen? */
  mikroRecht?: "granted" | "denied" | "undetermined" | "unbekannt";
  /** Build 106+: hält die App das Mikro beim Vorlesen offen? Fehlt in älteren Builds ⇒ aus. */
  mikroBeimVorlesen?: boolean;
}

/** Hi-Fi-Wiedergabe an/aus; liefert den RESULTIERENDEN Session-Zustand
 *  zurueck (null im Browser/alter Huelle). */
/**
 * Ticket 744 (2026-07-28): Meldet dem nativen Teil, ob das Display wach
 * bleiben soll. No-op im Browser/PWA — dort regelt das die WakeLock-API
 * allein.
 *
 * 28.09.2026 (Ticket 2754): Die erste Meldung kommt beim Start der Seite —
 * beim Kaltstart kann das Plugin da noch fehlen. Ohne Warten ginge genau
 * die Meldung verloren, die eine neue Seite der App schuldet.
 */
export async function setDisplayWach(wach: boolean): Promise<void> {
  const p = await warteAufRecorder();
  if (!p?.setDisplayWach) return;
  try {
    await p.setDisplayWach({ wach });
  } catch {
    /* nicht kritisch — im Zweifel bleibt das Display wach wie bisher */
  }
}

export async function setPlaybackHiFi(
  active: boolean,
): Promise<AudioSessionState | null> {
  const p = getRecorder();
  if (!p?.setPlaybackHiFi) return null;
  try {
    const st = await p.setPlaybackHiFi({ active });
    mikroBeimVorlesenMerken(st);
    return st;
  } catch {
    return null;
  }
}

/** Aktuellen AVAudioSession-Zustand abfragen (Diagnose). `rekorderMessung`
 *  siehe PawbotRecorderPlugin.getAudioSessionState. */
/** Der Gerätezustand der App — null im Browser oder in einer App vor Build 103. */
export async function geraeteZustand(): Promise<GeraeteZustandNativ | null> {
  const p = getRecorder();
  if (!p?.geraeteZustand) return null;
  try {
    return await p.geraeteZustand();
  } catch {
    return null;
  }
}

export async function getAudioSessionState(opts?: {
  rekorderMessung?: boolean;
}): Promise<AudioSessionState | null> {
  const p = getRecorder();
  if (!p?.getAudioSessionState) return null;
  try {
    const st = await p.getAudioSessionState(opts);
    mikroBeimVorlesenMerken(st);
    return st;
  } catch {
    return null;
  }
}

/**
 * Ticket 2643 (29.09.2026): die Einstellungen von Vespera öffnen — dort
 * liegt das Mikrofon-Recht. false im Browser und in App-Builds vor 93.
 */
export async function oeffneAppEinstellungen(): Promise<boolean> {
  const p = getRecorder();
  if (!p?.oeffneAppEinstellungen) return false;
  try {
    return (await p.oeffneAppEinstellungen()).ok === true;
  } catch {
    return false;
  }
}

export async function pushAlltagWidgetInfo(info: {
  lastTitle?: string;
  lastTimeHHMM?: string;
  countToday?: number;
  openTasks?: number;
  /** 2026-07-14: aufgeloester App-Modus — Widget folgt der App. */
  uiIsDark?: boolean;
  uiPalette?: string;
}): Promise<void> {
  try {
    const plugin = getRecorder();
    if (!plugin?.widgetUpdate) return;
    await plugin.widgetUpdate({ ...info, ...readWidgetColors() });
  } catch {
    /* alte Hülle / kein Plugin / native Hick-Ups: still bleiben */
  }
}

// ── Pause-Wache (2026-07-23) ────────────────────────────────────────────
// "Immer aufzeichnen" AN + Zustand "pausiert" darf nie dauerhaft sein.
// Egal WER pausiert hat (Mikro-Koordination, Interruption, verlorenes
// Resume): nach HEAL_MS wird fortgesetzt, solange der Tages-Schalter an
// ist und keine Mikro-Pause AKTIV laeuft (dann verlaengern, das Diktat
// hat Vorrang). Bewusstes Stoppen = Schalter AUS -> keine Heilung.
// 24.09.2026 (Ticket 2483): bei dunklem Display nur vorgemerkt, fortgesetzt
// wird beim nächsten Sichtbarwerden (siehe merkeFortsetzenVor).
const PAUSE_HEAL_MS = 3 * 60_000;
const PAUSE_HEAL_RETRY_MS = 60_000;
let pauseHealTimer: number | null = null;

function armPauseHeal(delay: number): void {
  if (pauseHealTimer) window.clearTimeout(pauseHealTimer);
  pauseHealTimer = window.setTimeout(() => {
    pauseHealTimer = null;
    void (async () => {
      try {
        if (getRecorderState() !== "paused") return;
        /*
         * ⚠️ 06.09.2026 — hier stand `if (!isDayIntentActive()) return;`
         *    ohne neuen Termin.
         *
         * Fehlt die native Absicht in genau diesem Augenblick (sie kommt
         * ueber die Zustands-Abgleiche und kann kurz fehlen), gab die Wache
         * auf. Geweckt wird sie danach nur von einem Zustandswechsel — und
         * der kommt nicht mehr, weil der Zustand ja schon „pausiert" ist.
         * Eine einzige Flanke genuegte, und die Aufnahme stand bis zum
         * naechsten Handgriff. Genau das hat Martin gemeldet.
         *
         * Jetzt: Wer durchgehend aufnehmen will, bekommt einen neuen Termin
         * statt eines stillen Endes.
         */
        if (!isDayIntentActive()) {
          let dauer = false;
          try {
            dauer = localStorage.getItem("voice-v8.alwaysRecord") === "1";
          } catch {
            /* kein Speicher — dann bleibt es beim alten Verhalten */
          }
          if (dauer) armPauseHeal(PAUSE_HEAL_RETRY_MS);
          return;
        }
        // 24.09.2026 (Ticket 2202): auch die native Sprechtaste zählt.
        if (hatOffenePausenGruende()) {
          // Diktat/PTT laeuft wirklich noch — nicht dazwischenfunken.
          armPauseHeal(PAUSE_HEAL_RETRY_MS);
          return;
        }
        /*
         * ⚠️ 24.09.2026 (Ticket 2483): bei dunklem Display nicht anstoßen,
         *    sondern vormerken. Ein `resume()` im Hintergrund verbrauchte nur
         *    die zehn Versuche der Engine (siehe merkeFortsetzenVor) — die
         *    Engine hat ihre eigene Pause-Frist und versucht es selbst.
         */
        if (imHintergrund()) {
          merkeFortsetzenVor("pause-wache");
          return;
        }
        // Dieselbe Regel wie die Wache und holeFortsetzenNach: ohne BELEGTE
        // Ursache bleibt die Pause stehen (sie koennte Martins sein).
        if (urteilFuerWache() !== "belegt") {
          meldeMikroAnlass(`fortsetzen-unterlassen:${urteilFuerWache()}`);
          return;
        }
        try {
          localStorage.removeItem(MIC_PAUSE_KEY);
        } catch {
          /* egal */
        }
        await setzeJetztFort();
        console.warn(
          "[recorder] Pause-Wache: Tagesaufnahme war pausiert ohne aktiven Grund — fortgesetzt",
        );
      } catch (err) {
        console.warn("[recorder] Pause-Wache Fehler", err);
        armPauseHeal(PAUSE_HEAL_RETRY_MS);
      }
    })();
  }, delay);
}

// 2026-09-01 (Martin): Alltag darf bei visibility hidden NICHT pausieren.
// Der native Recorder haelt die Session; JS darf hier nur abgleichen, nie
// pause()/stop() aus Sichtbarkeit ableiten. Chat-PTT ist ein anderer Weg.
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    void syncFromNative();
    /*
     * 26.09.2026 (Ticket 2615): Scheitert hinten der Häppchenwechsel, läuft
     * der alte Rekorder weiter — es gibt KEINE Pause, bei der die Messwerte
     * abgeholt würden. Beim Öffnen holen, sonst lägen sie bis zum nächsten
     * fremden Halt.
     */
    void getAudioSessionState({ rekorderMessung: true }).then((st) =>
      meldeRekorderMessung(st?.rekorderMessung),
    );
  });
}

/** Beim App-Boot aufrufen: heilt haengengebliebene Pausen dauerhaft. */
export function startDayRecorderPauseWatch(): void {
  window.addEventListener("voice-v8:recorder-state", (e) => {
    const st = (e as CustomEvent<{ state?: string }>).detail?.state;
    if (st === "paused") {
      if (!pauseHealTimer) armPauseHeal(PAUSE_HEAL_MS);
    } else if (pauseHealTimer) {
      window.clearTimeout(pauseHealTimer);
      pauseHealTimer = null;
    }
  });
  // Boot-Fall: Engine ist JETZT schon pausiert (z.B. App-Neustart mitten
  // in haengender Pause) — kurze Frist, der Nutzer wartet ja bereits.
  window.setTimeout(() => {
    if (getRecorderState() === "paused" && !pauseHealTimer) {
      armPauseHeal(20_000);
    }
  }, 4_000);
}


/* ═══════════════════════════════════════════════════════════════════════
 * Der Rueckfall ohne native Huelle. (17.09.2026)
 *
 * ⚠️ Bewusst KEIN zweiter Zustandsapparat. Der Browser-Rekorder meldet
 *    seine Zustaende ueber dieselbe `notifyState`, die auch die native
 *    Engine benutzt — Overlay, Statuszeile und Kopfleiste zeigen damit
 *    dasselbe, egal welcher Weg laeuft. Zwei Anzeigen fuer eine Aufnahme
 *    waeren genau das Muster, das in diesem Baum am haeufigsten schiefging.
 * ═══════════════════════════════════════════════════════════════════════ */

let browserRekorder: import("./browser-recorder.js").BrowserRecorder | null =
  null;

/** Laeuft die Aufnahme gerade ueber den Browser statt ueber die Huelle? */
export function laeuftImBrowser(): boolean {
  return browserRekorder?.getZustand() === "recording";
}

async function starteImBrowser(cb: RecorderCallbacks): Promise<boolean> {
  if (typeof navigator === "undefined" || !navigator.mediaDevices) return false;
  try {
    const [{ BrowserRecorder }, { echteUmgebung }] = await Promise.all([
      import("./browser-recorder.js"),
      import("./browser-recorder-umgebung.js"),
    ]);
    const rec = new BrowserRecorder(
      echteUmgebung((m) => {
        window.dispatchEvent(
          new CustomEvent("voice-v8:toast", {
            detail: { text: m.text, art: m.art },
          }),
        );
      }),
    );
    /*
     * ⚠️⚠️ 22.09.2026 — DERSELBE HANDSCHLAG WIE NATIV, UND ER FEHLTE HIER.
     *
     * Der native Weg (oben, :547-555) gibt das warme Mikro frei, BEVOR er
     * nach dem eigenen greift, und laesst WebKit 450 ms zur Ruhe kommen.
     * Der Grund steht dort: die Freigabe NACH dem Start riss die
     * Audio-Session mitten im Anlauf um.
     *
     * Dieser Weg hier tat es nie. `rec.start()` ruft `getUserMedia` mit
     * `echoCancellation: true`, waehrend der Warm-Strom aus `acquireRawMic`
     * (roh, ohne Verarbeitung) noch live ist — zwei konkurrierende Griffe
     * ans selbe Geraet. `dayRecorderBusy` wird erst DANACH gesetzt, ueber
     * `notifyState`; bis dahin weiss die Sprechtaste von nichts.
     *
     * ⚠️ Das Fenster gab es schon vorher: nach jedem Sprechtasten-Druck
     *    bleibt der Strom auch im Browser warm. Seit heute wird dort aber
     *    auch beim Seitenaufruf, beim Zurueckkommen und nach jeder Antwort
     *    vorgewaermt — aus dem seltenen Fall wird der Normalfall.
     */
    try {
      const detail: { held?: boolean } = {};
      window.dispatchEvent(
        new CustomEvent("voice-v8:recorder-will-start", { detail }),
      );
      if (detail.held) await new Promise((r) => setTimeout(r, 450));
    } catch {
      /* kein window (Tests) */
    }
    await rec.start();
    browserRekorder = rec;
    notifyState("recording");
    /*
     * ⚠️ Der Hoerer gehoert HIERHIN und nicht in den Rekorder: Er muss auch
     *    dann abgeraeumt werden, wenn die Aufnahme ueber einen anderen Weg
     *    endet. Ein Hoerer, den niemand loest, feuert beim naechsten Start
     *    ein zweites Mal.
     */
    const beiWechsel = (): void => {
      if (document.visibilityState === "hidden") void rec.beiHintergrund();
      if (rec.getZustand() === "idle") {
        document.removeEventListener("visibilitychange", beiWechsel);
        browserRekorder = null;
        notifyState("idle");
      }
    };
    document.addEventListener("visibilitychange", beiWechsel);
    return true;
  } catch (e) {
    cb.onError?.(e instanceof Error ? e : new Error(String(e)));
    return false;
  }
}
